import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  House, Money, CalendarDots, Trophy, CreditCard, SignOut,
  User, Clock, MapPin, CheckCircle, Warning, ArrowRight, Volleyball, Bell, BellRinging, Phone, Copy, ListChecks, Lock, FilePdf, UploadSimple, Image, Wallet, Target, Crown, TShirt, ShoppingCart, Lightning
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RankingTable } from '../components/RankingTable';
import { CourtRentalPortal } from '../components/CourtRentalPortal';
import LanguageToggle from '../components/LanguageToggle';
import { toast } from 'sonner';
import { generatePaymentReceipt } from '../utils/receiptGenerator';
import { OptionChips, autoPick, missingOption } from '../components/store/OptionChips';
import { ComboPickerDialog } from '../components/store/ComboPickerDialog';
import { CartLineOptions } from '../components/store/OrderItemsDetail';
import { MyOrders } from '../components/store/MyOrders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const storeEffUnit = (basePrice, tiers, qty) => {
  let best = Number(basePrice) || 0, bestMin = 1;
  (tiers || []).forEach((t) => {
    const mq = parseInt(t.min_qty), up = parseFloat(t.unit_price);
    if (mq >= 1 && up > 0 && qty >= mq && mq >= bestMin) { bestMin = mq; best = up; }
  });
  return best;
};
const LOGO_URL = "/logo-tk.png";

function FuncionalPortal({ user, profile, refresh }) {
  const { t, i18n } = useTranslation();
  const [info, setInfo] = useState(null);
  const [tab, setTab] = useState('avulsa');
  const [category, setCategory] = useState('adulto'); // adulto | kids
  const [kidName, setKidName] = useState('');
  const [date, setDate] = useState('');
  const [plan, setPlan] = useState('1x');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('idle'); // idle | zelle | success
  const [regId, setRegId] = useState(null);
  const [subId, setSubId] = useState(null);
  const [subPlanState, setSubPlanState] = useState('1x');
  const [uploading, setUploading] = useState(false);
  const [isFirstTrial, setIsFirstTrial] = useState(false);
  const [firstTrialEligible, setFirstTrialEligible] = useState(true);

  useEffect(() => { axios.get(`${API}/funcional/info`).then(r => setInfo(r.data)).catch(() => {}); }, []);

  const studentEmail = user?.email || '';
  const studentName = profile?.student?.name || user?.name || '';
  const studentPhone = profile?.student?.phone || '';

  // Check first-trial eligibility (universal: funcional + futevolei)
  useEffect(() => {
    if (!studentEmail && !studentPhone) return;
    const params = new URLSearchParams();
    if (studentEmail) params.set('email', studentEmail);
    if (studentPhone) params.set('phone', studentPhone);
    axios.get(`${API}/first-trial/eligible?${params.toString()}`)
      .then(r => setFirstTrialEligible(!!r.data.eligible))
      .catch(() => setFirstTrialEligible(false));
  }, [studentEmail, studentPhone]);

  // Reset isFirstTrial if not avulsa
  useEffect(() => { if (tab !== 'avulsa') setIsFirstTrial(false); }, [tab]);

  // 2 past + 4 upcoming of each category-specific weekday — past dates allowed for late registrations
  const nextDays = (wd) => {
    const out = [];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const c = new Date(today);
    c.setDate(c.getDate() + ((wd - c.getDay() + 7) % 7));
    c.setDate(c.getDate() - 2 * 7); // go back 2 weeks
    for (let i = 0; i < 6; i++) {
      const yyyy = c.getFullYear(), mm = String(c.getMonth() + 1).padStart(2, '0'), dd = String(c.getDate()).padStart(2, '0');
      out.push(`${yyyy}-${mm}-${dd}`);
      c.setDate(c.getDate() + 7);
    }
    return out;
  };
  // Adulto: Tue(2) + Thu(4). Kids: Mon(1) + Wed(3)
  const allowedWeekdays = category === 'kids' ? [1, 3] : [2, 4];
  const allowedDates = [...nextDays(allowedWeekdays[0]), ...nextDays(allowedWeekdays[1])].sort();
  const fmtBR = (ymd) => { const [y, m, d] = ymd.split('-'); return `${d}/${m}/${y}`; };

  const dateErr = date && (() => {
    const [y, m, d] = date.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return allowedWeekdays.includes(dt.getDay()) ? null : (category === 'kids' ? 'Kids: apenas segunda ou quarta' : 'Adulto: apenas terca ou quinta');
  })();
  const planPrice = (info?.plans || []).find(p => p.id === plan)?.price || 70;
  // Total: if avulsa + first-trial eligible -> $15. Else avulsa -> $30. Else plan.
  const total = tab === 'avulsa' ? (isFirstTrial ? 15 : 30) : planPrice;
  const horarios = category === 'kids' ? 'Segunda e Quarta 19:00 (7PM).' : 'Terca e Quinta 19:00 (7PM).';
  const kidsBlocked = category === 'kids' && !kidName.trim();

  const payCard = async () => {
    setLoading(true);
    try {
      if (tab === 'avulsa') {
        const { data: reg } = await axios.post(`${API}/funcional/avulsa/register`, {
          name: studentName, phone: studentPhone, email: studentEmail, preferred_date: date,
          category, kid_name: category === 'kids' ? kidName : undefined,
          is_first_trial: isFirstTrial
        });
        const { data: ck } = await axios.post(`${API}/funcional/avulsa/checkout`, { registration_id: reg.id, origin_url: window.location.origin });
        if (ck.url) window.location.assign(ck.url);
      } else {
        const { data: ck } = await axios.post(`${API}/funcional/subscription/checkout`, {
          name: studentName, email: studentEmail, phone: studentPhone, plan, origin_url: window.location.origin,
          category, kid_name: category === 'kids' ? kidName : undefined
        });
        if (ck.url) window.location.assign(ck.url);
      }
    } catch (e) { toast.error(e.response?.data?.detail || t('portal.errorGeneric')); setLoading(false); }
  };
  const payZelle = async () => {
    setLoading(true);
    try {
      if (tab === 'avulsa') {
        const { data: reg } = await axios.post(`${API}/funcional/avulsa/register`, {
          name: studentName, phone: studentPhone, email: studentEmail, preferred_date: date,
          category, kid_name: category === 'kids' ? kidName : undefined,
          is_first_trial: isFirstTrial
        });
        setRegId(reg.id); setSubId(null); setStep('zelle');
      } else {
        const { data: sub } = await axios.post(`${API}/funcional/subscription/zelle`, {
          name: studentName, email: studentEmail, phone: studentPhone, plan,
          category, kid_name: category === 'kids' ? kidName : undefined
        });
        setSubId(sub.id); setRegId(null); setSubPlanState(plan); setStep('zelle');
      }
    } catch (e) { toast.error(e.response?.data?.detail || t('portal.errorGeneric')); }
    setLoading(false);
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      if (regId) await axios.post(`${API}/funcional/avulsa/zelle-receipt?registration_id=${regId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      else if (subId) await axios.post(`${API}/funcional/subscription/zelle-receipt?subscription_id=${subId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('portal.paymentConfirmed'));
      setStep('success'); refresh && refresh();
    } catch (e) { toast.error(e.response?.data?.detail || t('portal.errorGeneric')); }
    setUploading(false);
  };

  return (
    <div className="bg-white border border-[#E6E2D8] rounded-lg p-6" data-testid="funcional-portal">
      <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-1 flex items-center gap-2">
        <Lightning size={20} className="text-[#0077B6]" weight="fill" /> {t('funcional.title')}
      </h2>
      <p className="text-sm text-[#73706A] mb-4">{horarios}</p>

      {step === 'success' ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircle size={48} className="text-[#2D6A4F] mx-auto mb-3" weight="fill" />
          <p className="font-semibold text-[#2D6A4F] text-lg">Confirmado!</p>
          <Button onClick={() => { setStep('idle'); setRegId(null); setSubId(null); setDate(''); }} className="mt-4 bg-[#0077B6] text-white" data-testid="func-portal-new">{i18n.language === 'en' ? 'New registration' : (i18n.language === 'en' ? 'New registration' : 'Nova inscricao')}</Button>
        </div>
      ) : step === 'zelle' ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <p className="text-sm mb-2">Envie <strong className="text-[#0077B6]">${(regId ? (isFirstTrial ? 15 : 30) : (info?.plans?.find(p => p.id === subPlanState)?.price || 70)).toFixed(2)}</strong> via Zelle:</p>
          <div className="bg-white rounded p-3 mb-3 flex items-center justify-between">
            <span className="font-mono text-lg font-bold">5618596830</span>
            <button onClick={() => { navigator.clipboard.writeText('5618596830'); toast.success(t('portal.copied')); }} className="text-[#0077B6]"><Copy size={20} /></button>
          </div>
          <label className="block">
            <span className="text-sm font-medium">{t('portal.uniformReceipt')} *</span>
            <input type="file" accept="application/pdf,image/*" onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} className="mt-1 block w-full text-sm border border-[#E6E2D8] rounded p-2 file:mr-2 file:px-3 file:py-1 file:rounded file:border-0 file:bg-[#0077B6] file:text-white" data-testid="func-portal-zelle-upload" />
          </label>
          <button onClick={() => { setStep('idle'); setRegId(null); setSubId(null); }} className="mt-3 text-sm text-[#73706A]">{t('portal.cancel')}</button>
        </div>
      ) : (
        <>
          {/* Category selector */}
          <div className="grid grid-cols-2 gap-1 bg-[#F5F2EB] rounded-lg p-1 mb-3">
            <button onClick={() => { setCategory('adulto'); setDate(''); setKidName(''); }} className={`py-2 rounded text-sm font-medium ${category === 'adulto' ? 'bg-white shadow-sm' : 'text-[#73706A]'}`} data-testid="func-portal-cat-adulto">Adulto</button>
            <button onClick={() => { setCategory('kids'); setDate(''); }} className={`py-2 rounded text-sm font-medium ${category === 'kids' ? 'bg-white shadow-sm' : 'text-[#73706A]'}`} data-testid="func-portal-cat-kids">Kids</button>
          </div>

          {category === 'kids' && (
            <div className="mb-3">
              <Label className="text-sm">{t('funcional.kidName')} *</Label>
              <Input value={kidName} onChange={(e) => setKidName(e.target.value)} placeholder={t('portal.kidNamePh')} data-testid="func-portal-kid-name" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-1 bg-[#F5F2EB] rounded-lg p-1 mb-4">
            <button onClick={() => setTab('avulsa')} className={`py-2 rounded text-sm font-medium ${tab === 'avulsa' ? 'bg-white shadow-sm' : 'text-[#73706A]'}`} data-testid="func-portal-tab-avulsa">Avulsa $30</button>
            <button onClick={() => setTab('subscription')} className={`py-2 rounded text-sm font-medium ${tab === 'subscription' ? 'bg-white shadow-sm' : 'text-[#73706A]'}`} data-testid="func-portal-tab-sub">Mensal 1x/2x</button>
          </div>

          {tab === 'avulsa' && firstTrialEligible && (
            <label className="flex items-center gap-2 p-3 mb-3 border-2 border-[#FFB703] bg-[#FFF3E6] rounded-lg cursor-pointer" data-testid="func-portal-first-trial-label">
              <input type="checkbox" checked={isFirstTrial} onChange={(e) => setIsFirstTrial(e.target.checked)} className="w-4 h-4 accent-[#E85D04]" data-testid="func-portal-first-trial" />
              <span className="text-sm">
                <strong className="text-[#E85D04]">{t('dropIn.firstTrialTitle')} - $15</strong>
                <span className="text-[#73706A] block text-xs">{i18n.language === 'en' ? 'Available only once per student' : 'Disponivel apenas uma vez por aluno'}</span>
              </span>
            </label>
          )}

          {tab === 'avulsa' ? (
            <div className="mb-4">
              <Label className="text-sm">{t('common.preferredDate')} *</Label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`mt-1 block w-full border rounded-lg p-2 ${dateErr ? 'border-[#9B2226]' : 'border-[#E6E2D8]'}`} data-testid="func-portal-date" />
              {dateErr && <p className="text-xs text-[#9B2226] mt-1">{dateErr}</p>}
              <div className="mt-2 flex flex-wrap gap-1">
                {allowedDates.map(d => (
                  <button key={d} type="button" onClick={() => setDate(d)} className={`text-[11px] px-2 py-1 rounded border ${date === d ? 'bg-[#0077B6] border-[#0077B6] text-white' : 'bg-white border-[#E6E2D8]'}`}>{fmtBR(d)}</button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-4">
              <Label className="text-sm">Plano *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {(info?.plans || []).map(p => (
                  <button key={p.id} type="button" onClick={() => setPlan(p.id)} className={`p-3 border rounded-lg text-center ${plan === p.id ? 'border-[#0077B6] bg-blue-50' : 'border-[#E6E2D8]'}`} data-testid={`func-portal-plan-${p.id}`}>
                    <p className="text-sm font-semibold">{p.label}</p>
                    <p className="text-xl font-bold text-[#0077B6]">${p.price.toFixed(2)}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="bg-[#F5F2EB] rounded-lg p-3 text-center mb-3">
            <p className="text-xs text-[#73706A]">{i18n.language === 'en' ? 'Total' : 'Total'}</p>
            <p className="text-2xl font-bold text-[#0077B6]" data-testid="func-portal-total">${total.toFixed(2)}</p>
            <p className="text-[10px] text-[#73706A]">Cartao: ${(total * 1.05).toFixed(2)} | Zelle: ${total.toFixed(2)}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Button onClick={payCard} disabled={loading || kidsBlocked || (tab === 'avulsa' && (!date || dateErr))} className="bg-[#0077B6] text-white" data-testid="func-portal-card-btn">
              <CreditCard size={16} className="mr-2" /> Cartao
            </Button>
            <Button onClick={payZelle} disabled={loading || kidsBlocked || (tab === 'avulsa' && (!date || dateErr))} variant="outline" className="border-[#0077B6] text-[#0077B6]" data-testid="func-portal-zelle-btn">
              <Phone size={16} className="mr-2" /> Zelle
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


function PortalDuplasConfirmadas() {
  const { t, i18n } = useTranslation();
  const [duplas, setDuplas] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/league/duplas`).then(r => setDuplas(r.data || [])).catch(() => {});
  }, []);

  const cats = [
    { value: 'misto', label: 'Misto' },
    { value: 'open', label: 'Open' },
    { value: 'feminino', label: 'Feminino' },
    { value: 'rei_rainha', label: 'Rei e Rainha' },
  ];
  const counts = cats.reduce((acc, c) => { acc[c.value] = duplas.filter(d => d.category === c.value).length; return acc; }, {});
  const visible = filter === 'all' ? duplas : duplas.filter(d => d.category === filter);
  const grouped = visible.reduce((acc, d) => { (acc[d.category] = acc[d.category] || []).push(d); return acc; }, {});
  const catLabel = (v) => cats.find(c => c.value === v)?.label || v;

  if (duplas.length === 0) return null;

  return (
    <div className="bg-white border border-[#E6E2D8] rounded-lg p-6" data-testid="portal-duplas-confirmadas">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] flex items-center gap-2">
            <Trophy size={20} className="text-[#E85D04]" />
            Duplas Confirmadas
          </h2>
          <p className="text-sm text-[#73706A]">Duplas oficiais por categoria.</p>
        </div>
        <div className="flex flex-wrap items-center gap-1 bg-[#F5F2EB] rounded-lg p-1">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded text-xs font-medium ${filter === 'all' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`}
            data-testid="portal-duplas-filter-all"
          >
            Todas ({duplas.length})
          </button>
          {cats.map(c => counts[c.value] === 0 ? null : (
            <button
              key={c.value}
              onClick={() => setFilter(c.value)}
              className={`px-3 py-1.5 rounded text-xs font-medium ${filter === c.value ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`}
              data-testid={`portal-duplas-filter-${c.value}`}
            >
              {c.label} ({counts[c.value]})
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        {Object.entries(grouped).map(([cat, list]) => (
          <div key={cat}>
            <h3 className="text-xs font-semibold text-[#E85D04] uppercase tracking-wide mb-2">{catLabel(cat)}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {list.map((d, i) => (
                <div key={d.id} className="flex items-center gap-3 p-3 bg-[#F5F2EB] rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-[#E85D04] text-white flex items-center justify-center text-xs font-bold">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#1A1A1A]"><strong>{d.player1}</strong></p>
                    <p className="text-sm text-[#1A1A1A]"><strong>{d.player2}</strong></p>
                    {d.notes && <p className="text-[10px] text-[#73706A] truncate">{d.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function MyTurmasCard() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/portal/turmas`, { withCredentials: true });
      setData(data);
      setSelected(data.selecionadas || []);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const byDay = {};
  (data.turmas_disponiveis || []).forEach(t => {
    byDay[t.dia] = byDay[t.dia] || [];
    byDay[t.dia].push(t);
  });
  const days = ['Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'];

  const toggle = (id) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= data.limite) {
        toast.error(`Seu plano ${data.plano}/semana permite no maximo ${data.limite} turma(s). Faca upgrade na aba Mensalidades.`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/portal/turmas`, { turmas: selected }, { withCredentials: true });
      toast.success(t('portal.classesUpdated'));
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('portal.processError'));
    } finally {
      setSaving(false);
    }
  };

  const byId = Object.fromEntries((data.turmas_disponiveis || []).map(t => [t.id, t]));
  const currentLabels = (data.selecionadas || []).map(id => {
    const t = byId[id];
    return t ? `${t.dia} ${t.horario} - ${t.turma}` : id;
  });

  return (
    <div className="bg-white border border-[#E6E2D8] rounded-lg p-6" data-testid="my-turmas-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] flex items-center gap-2">
            <CalendarDots size={20} className="text-[#E85D04]" />
            {t('portal.minhasTurmasTitle')}
          </h2>
          <p className="text-sm text-[#73706A] mt-1">
            {t('portal.planLabel')} {data.plano}/{t('register.perWeek') === 'x per week' ? 'week' : 'semana'} — {t('portal.uptoClasses', { n: data.limite, c: data.selecionadas.length })}
          </p>
          <p
            className="text-[11px] text-[#73706A] mt-2 italic"
            data-testid="monthly-basis-note"
            dangerouslySetInnerHTML={{
              __html: t('portal.monthlyBasisNote', {
                count: data.plano === '3x' ? 12 : data.plano === '2x' ? 8 : 4
              })
            }}
          />
        </div>
        {!editing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
            className="border-[#E85D04] text-[#E85D04] hover:bg-orange-50"
            data-testid="edit-my-turmas-btn"
          >
            {t('portal.saveClasses') === 'Save classes' ? 'Edit classes' : 'Editar turmas'}
          </Button>
        )}
      </div>

      {!editing ? (
        currentLabels.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {currentLabels.map((label, i) => (
              <span key={i} className="bg-orange-50 border border-orange-200 rounded-full px-3 py-1 text-xs text-[#1A1A1A]" data-testid="my-turma-chip">
                {label}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#9B2226]">{t('portal.noClassesPicked')}</p>
        )
      ) : (
        <div>
          <p className="text-xs text-[#73706A] mb-2">
            Marque ate <strong>{data.limite}</strong> turma(s). Selecionadas: <strong>{selected.length}</strong>
          </p>
          <div className="space-y-3 max-h-72 overflow-y-auto border border-[#E6E2D8] rounded-lg p-3 bg-[#FDFBF7]">
            {days.map(day => {
              const list = byDay[day] || [];
              if (list.length === 0) return null;
              return (
                <div key={day}>
                  <p className="text-xs font-semibold text-[#E85D04] uppercase tracking-wide mb-1">{day}</p>
                  <div className="space-y-1">
                    {list.map(t => {
                      const checked = selected.includes(t.id);
                      const disabled = !checked && selected.length >= data.limite;
                      return (
                        <label
                          key={t.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer ${
                            checked ? 'bg-orange-50' : disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white'
                          }`}
                          data-testid={`my-turma-option-${t.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => toggle(t.id)}
                            className="w-4 h-4 accent-[#E85D04]"
                          />
                          <span className="text-xs text-[#1A1A1A]">
                            {t.horario} - {t.turma}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
              data-testid="save-my-turmas-btn"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button
              variant="outline"
              onClick={() => { setEditing(false); setSelected(data.selecionadas || []); }}
              disabled={saving}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function PortalPasswordChange() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) { toast.error(t('portal.passwordMismatch')); return; }
    if (form.new_password.length < 6) { toast.error(t('portal.passwordMin')); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: form.current_password, new_password: form.new_password });
      toast.success(t('portal.passwordChanged'));
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) { toast.error(e.response?.data?.detail || t('portal.passwordChangeError')); }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-[#E6E2D8] rounded-lg p-6 max-w-md">
      <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
        <Lock size={20} className="text-[#E85D04]" /> {i18n.language === 'en' ? 'Change Password' : 'Alterar Senha'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#1A1A1A] mb-1">{i18n.language === 'en' ? 'Current password' : (i18n.language === 'en' ? 'Current password' : 'Senha atual')}</label>
          <input type={showPw ? 'text' : 'password'} value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="input-field" required data-testid="portal-current-pw" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#1A1A1A] mb-1">{i18n.language === 'en' ? 'New password' : (i18n.language === 'en' ? 'New password' : 'Nova senha')}</label>
          <input type={showPw ? 'text' : 'password'} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="input-field" placeholder={i18n.language === 'en' ? 'Min 6 characters' : 'Mínimo 6 caracteres'} required data-testid="portal-new-pw" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#1A1A1A] mb-1">{i18n.language === 'en' ? 'Confirm new password' : (i18n.language === 'en' ? 'Confirm new password' : 'Confirmar nova senha')}</label>
          <input type="password" value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} className="input-field" required data-testid="portal-confirm-pw" />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={showPw} onChange={() => setShowPw(!showPw)} className="accent-[#E85D04]" />
          <span className="text-sm text-[#73706A]">{i18n.language === 'en' ? 'Show passwords' : 'Mostrar senhas'}</span>
        </div>
        <Button type="submit" disabled={saving} className="bg-[#E85D04] hover:bg-[#DC2F02] text-white w-full" data-testid="portal-change-pw-btn">
          {saving ? 'Alterando...' : 'Alterar Senha'}
        </Button>
      </form>
    </div>
  );
}


export default function StudentPortal() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [payments, setPayments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [stripeLoading, setStripeLoading] = useState(null);
  const [pollingPayment, setPollingPayment] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [trialCredit, setTrialCredit] = useState({ has_credit: false, credit_value: 0 });
  const [subLoading, setSubLoading] = useState(false);
  const [leagueCategories, setLeagueCategories] = useState([]);
  const [rankingFem, setRankingFem] = useState([]);
  const [rankingMasc, setRankingMasc] = useState([]);
  const [membership, setMembership] = useState({ price: 85, label: 'Membership Open Play', description: '' });
  const [membershipStep, setMembershipStep] = useState('idle'); // idle | zelle-upload | success
  const [membershipOrderId, setMembershipOrderId] = useState(null);
  const [membershipSaving, setMembershipSaving] = useState(false);
  const [uploadingMembershipReceipt, setUploadingMembershipReceipt] = useState(false);
  const [dayPassSaving, setDayPassSaving] = useState(false);
  const [checkoutRedirectUrl, setCheckoutRedirectUrl] = useState(null);
  const checkoutTimerRef = useRef(null);

  const goToCheckout = (url) => {
    // Robusto contra navegadores in-app (Instagram/Facebook/WhatsApp) que bloqueiam
    // redirect automatico: mostra overlay com link nativo clicavel e tenta redirecionar.
    setCheckoutRedirectUrl(url);
    checkoutTimerRef.current = setTimeout(() => { try { window.location.assign(url); } catch { /* fallback: botao manual */ } }, 700);
  };

  const cancelCheckout = () => {
    if (checkoutTimerRef.current) { clearTimeout(checkoutTimerRef.current); checkoutTimerRef.current = null; }
    setCheckoutRedirectUrl(null);
    setMembershipSaving(false);
    setDayPassSaving(false);
  };

  const handleOpenPlayDay = async () => {
    setDayPassSaving(true);
    try {
      const { data: reg } = await axios.post(`${API}/open-play/register`, {
        name: profile?.student?.name || user?.name, email: user?.email, phone: profile?.student?.phone || user?.phone || '', preferred_date: dayPassDate,
      });
      const { data: checkout } = await axios.post(`${API}/open-play/checkout`, { registration_id: reg.id, origin_url: window.location.origin });
      if (checkout.url) { goToCheckout(checkout.url); return; }
      setDayPassSaving(false);
    } catch { toast.error('Erro ao iniciar pagamento'); setDayPassSaving(false); }
  };

  const [dayPassRegId, setDayPassRegId] = useState(null);
  const [dayPassDate, setDayPassDate] = useState(new Date().toISOString().slice(0, 10));
  const [portalEvents, setPortalEvents] = useState([]);
  useEffect(() => { axios.get(`${API}/paid-events`).then(({ data }) => setPortalEvents(data)).catch(() => {}); }, []);

  const handleOpenPlayDayZelle = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const { data: reg } = await axios.post(`${API}/open-play/register`, {
        name: profile?.student?.name || user?.name, email: user?.email, phone: profile?.student?.phone || user?.phone || '', preferred_date: dayPassDate,
      });
      setDayPassRegId(reg.id);
      toast.info('Envie o comprovante do Zelle abaixo para confirmar.');
    } catch { toast.error('Erro ao registrar'); }
  };
  const onOpenPlayZelleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !dayPassRegId) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/open-play/zelle-receipt?registration_id=${dayPassRegId}`, fd);
      toast.success('Comprovante enviado! Open Play do dia registrado.');
      setDayPassRegId(null);
    } catch { toast.error('Erro ao enviar comprovante'); }
  };
  const [leagueNotes, setLeagueNotes] = useState('');
  const [leagueSaving, setLeagueSaving] = useState(false);
  const [leagueSuccess, setLeagueSuccess] = useState(false);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [enrolling, setEnrolling] = useState(null);
  const [uploadingZelle, setUploadingZelle] = useState(null);
  const [leagueRegId, setLeagueRegId] = useState(null);
  const [uploadingLeagueReceipt, setUploadingLeagueReceipt] = useState(false);
  const [allMyPayments, setAllMyPayments] = useState([]);
  const [leagueStep, setLeagueStep] = useState('form');
  const [storeProducts, setStoreProducts] = useState([]);
  const [storeCombos, setStoreCombos] = useState([]);
  const [portalComboPicking, setPortalComboPicking] = useState(null);
  const [storeActiveImg, setStoreActiveImg] = useState({});
  const [cart, setCart] = useState([]);
  const cartCount = cart.reduce((s, c) => s + (c.quantity || 1), 0);
  const openPortalCart = () => {
    const el = document.getElementById('portal-cart');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else toast.info('Seu carrinho está vazio. Escolha tamanho e cor e toque em Adicionar.');
  };
  const [storePaymentMode, setStorePaymentMode] = useState('full'); // full | deposit
  const [storeZelleOrderId, setStoreZelleOrderId] = useState(null);
  const [uploadingStoreZelle, setUploadingStoreZelle] = useState(false);
  const [orderPlacing, setOrderPlacing] = useState(false); // form, zelle-upload
  const [uniformSize, setUniformSize] = useState('M');
  const [uniformSaving, setUniformSaving] = useState(false);
  const [uniformStep, setUniformStep] = useState('form'); // form | zelle-upload | success
  const [uniformOrderId, setUniformOrderId] = useState(null);
  const [uploadingUniformReceipt, setUploadingUniformReceipt] = useState(false);
  const [avulsas, setAvulsas] = useState([]);
  const [avulsaSaving, setAvulsaSaving] = useState(null); // 'avulsa' | 'fem' | null
  const [avulsaDates, setAvulsaDates] = useState({ avulsa: '', fem: '', jogo: '' });
  const [avulsaStep, setAvulsaStep] = useState('idle'); // idle | zelle-upload | success
  const [avulsaRegId, setAvulsaRegId] = useState(null);
  const [uploadingAvulsaReceipt, setUploadingAvulsaReceipt] = useState(false);

  // weekday: 0=Sun, 1=Mon, ... 5=Fri, 6=Sat (JS Date.getDay)
  const AVULSA_TYPES = {
    avulsa: { label: 'Aula Avulsa', price: 25, cardPrice: 26.25, color: '#0077B6', endpointBase: 'drop-in', allowedDay: null, dayName: 'qualquer dia' },
    fem: { label: 'Aula Avulsa Feminino (Sexta)', price: 20, cardPrice: 21.00, color: '#9B2226', endpointBase: 'drop-in-fem', allowedDay: 5, dayName: 'sexta-feira' },
  };

  // Returns past + upcoming occurrences (yyyy-mm-dd) of `weekday` (0=Sun..6=Sat). Past dates allowed for late registrations.
  const getNextWeekdays = (weekday, count = 8, pastCount = 2) => {
    const out = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let cursor = new Date(today);
    const diff = (weekday - cursor.getDay() + 7) % 7;
    cursor.setDate(cursor.getDate() + diff);
    cursor.setDate(cursor.getDate() - pastCount * 7);
    for (let i = 0; i < count + pastCount; i++) {
      const yyyy = cursor.getFullYear();
      const mm = String(cursor.getMonth() + 1).padStart(2, '0');
      const dd = String(cursor.getDate()).padStart(2, '0');
      out.push(`${yyyy}-${mm}-${dd}`);
      cursor.setDate(cursor.getDate() + 7);
    }
    return out;
  };

  const formatDateBR = (yyyymmdd) => {
    if (!yyyymmdd) return '';
    const [y, m, d] = yyyymmdd.split('-');
    return `${d}/${m}/${y}`;
  };

  // Validates a date string against the type's allowed weekday. Returns null if OK, or error message.
  const validateAvulsaDate = (typeKey, dateStr) => {
    if (!dateStr) return 'Escolha uma data';
    const cfg = AVULSA_TYPES[typeKey];
    if (cfg.allowedDay === null) return null;
    // Parse as local (avoid TZ shift)
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    if (dt.getDay() !== cfg.allowedDay) {
      return `${cfg.label} so acontece em ${cfg.dayName}. Escolha uma ${cfg.dayName}.`;
    }
    // Past dates allowed for late registration (people training and registering later)
    return null;
  };

  const handleAvulsaCard = async (typeKey) => {
    const cfg = AVULSA_TYPES[typeKey];
    const date = avulsaDates[typeKey];
    const err = validateAvulsaDate(typeKey, date);
    if (err) { toast.error(err); return; }
    setAvulsaSaving(typeKey);
    try {
      const { data: reg } = await axios.post(`${API}/${cfg.endpointBase}/register`, {
        name: profile?.student?.name || user?.name,
        email: user?.email,
        phone: profile?.student?.phone || user?.phone || '',
        preferred_date: date,
      });
      const { data: checkout } = await axios.post(`${API}/${cfg.endpointBase}/checkout`, {
        registration_id: reg.id,
        origin_url: window.location.origin
      });
      if (checkout.url) {
        window.location.assign(checkout.url);
      } else {
        toast.error(t('portal.checkoutError'));
        setAvulsaSaving(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('portal.processError'));
      setAvulsaSaving(null);
    }
  };

  const handleAvulsaZelle = async (typeKey) => {
    const cfg = AVULSA_TYPES[typeKey];
    const date = avulsaDates[typeKey];
    const err = validateAvulsaDate(typeKey, date);
    if (err) { toast.error(err); return; }
    setAvulsaSaving(typeKey);
    try {
      const { data: reg } = await axios.post(`${API}/${cfg.endpointBase}/register`, {
        name: profile?.student?.name || user?.name,
        email: user?.email,
        phone: profile?.student?.phone || user?.phone || '',
        preferred_date: date,
      });
      setAvulsaRegId({ id: reg.id, type: typeKey });
      setAvulsaStep('zelle-upload');
      toast.success(t('portal.registrationSubmitted'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('portal.processError'));
    } finally {
      setAvulsaSaving(null);
    }
  };

  const handleAvulsaReceiptUpload = async (file) => {
    if (!file || !avulsaRegId) return;
    const cfg = AVULSA_TYPES[avulsaRegId.type];
    setUploadingAvulsaReceipt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/${cfg.endpointBase}/zelle-receipt?registration_id=${avulsaRegId.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('portal.receiptSent'));
      setAvulsaStep('success');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('portal.receiptSendError'));
    } finally {
      setUploadingAvulsaReceipt(false);
    }
  };

  const handleZelleUpload = async (paymentId, file) => {
    if (!file) return;
    setUploadingZelle(paymentId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(
        `${API}/portal/zelle-receipt?payment_id=${paymentId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      );
      toast.success(data.message || t('common.receiptSent'));
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('portal.receiptSendError'));
    } finally {
      setUploadingZelle(null);
    }
  };

  const handleLeagueReceiptUpload = async (file) => {
    if (!file || !leagueRegId) return;
    setUploadingLeagueReceipt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/league/zelle-receipt?registration_id=${leagueRegId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('portal.receiptSent'));
      setLeagueSuccess(true);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('portal.receiptSendError'));
    } finally {
      setUploadingLeagueReceipt(false);
    }
  };

  const handleUniformCard = async () => {
    setUniformSaving(true);
    try {
      const { data: order } = await axios.post(`${API}/league/uniform`, {
        name: profile?.student?.name || user?.name,
        email: user?.email,
        phone: profile?.student?.phone || user?.phone || '',
        size: uniformSize
      });
      const { data: checkout } = await axios.post(`${API}/league/uniform/checkout`, {
        order_id: order.id,
        origin_url: window.location.origin
      });
      if (checkout.url) {
        window.location.assign(checkout.url);
      } else {
        toast.error(t('portal.checkoutError'));
        setUniformSaving(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('portal.processError'));
      setUniformSaving(false);
    }
  };

  const handleUniformZelle = async () => {
    setUniformSaving(true);
    try {
      const { data } = await axios.post(`${API}/league/uniform`, {
        name: profile?.student?.name || user?.name,
        email: user?.email,
        phone: profile?.student?.phone || user?.phone || '',
        size: uniformSize
      });
      setUniformOrderId(data.id);
      navigator.clipboard?.writeText('5618596830');
      toast.success('Pedido registrado! Envie $35.00 via Zelle para 5618596830');
      setUniformStep('zelle-upload');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('portal.errorGeneric'));
    } finally {
      setUniformSaving(false);
    }
  };

  const handleUniformReceiptUpload = async (file) => {
    if (!file || !uniformOrderId) return;
    setUploadingUniformReceipt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/league/uniform/zelle-receipt?order_id=${uniformOrderId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('common.receiptSent'));
      setUniformStep('success');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('portal.receiptSendError'));
    } finally {
      setUploadingUniformReceipt(false);
    }
  };

  const handleMembershipCard = async () => {
    setMembershipSaving(true);
    try {
      const { data: order } = await axios.post(`${API}/membership/open-play/order`, {
        name: profile?.student?.name || user?.name,
        email: user?.email,
        phone: profile?.student?.phone || user?.phone || ''
      });
      const { data: checkout } = await axios.post(`${API}/membership/open-play/checkout`, {
        order_id: order.id,
        origin_url: window.location.origin
      });
      if (checkout.url) {
        goToCheckout(checkout.url);
      } else {
        toast.error(t('portal.checkoutError'));
        setMembershipSaving(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('portal.errorGeneric'));
      setMembershipSaving(false);
    }
  };

  const handleMembershipZelle = async () => {
    setMembershipSaving(true);
    try {
      const { data } = await axios.post(`${API}/membership/open-play/order`, {
        name: profile?.student?.name || user?.name,
        email: user?.email,
        phone: profile?.student?.phone || user?.phone || ''
      });
      setMembershipOrderId(data.id);
      navigator.clipboard?.writeText('5618596830');
      toast.success(`Pedido registrado! Envie $${(membership.price || 0).toFixed(2)} via Zelle para 5618596830`);
      setMembershipStep('zelle-upload');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('portal.errorGeneric'));
    } finally {
      setMembershipSaving(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, paymentsRes, classesRes, eventsRes, notifsRes, subRes, availRes, allPayRes, productsRes, rankFemRes, rankMascRes, memRes, avulsasRes] = await Promise.all([
        axios.get(`${API}/portal/profile`, { withCredentials: true }),
        axios.get(`${API}/portal/payments`, { withCredentials: true }),
        axios.get(`${API}/portal/classes`, { withCredentials: true }),
        axios.get(`${API}/portal/events`, { withCredentials: true }),
        axios.get(`${API}/portal/notifications`, { withCredentials: true }),
        axios.get(`${API}/subscriptions/my-subscription`, { withCredentials: true }).catch(() => ({ data: { has_subscription: false } })),
        axios.get(`${API}/portal/available-classes`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/all-payments`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/products`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/league/rankings?gender=feminino`).catch(() => ({ data: { players: [] } })),
        axios.get(`${API}/league/rankings?gender=masculino`).catch(() => ({ data: { players: [] } })),
        axios.get(`${API}/membership/open-play`).catch(() => ({ data: { price: 85, label: 'Membership Open Play', description: '' } })),
        axios.get(`${API}/portal/avulsas`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setProfile(profileRes.data);
      setPayments(paymentsRes.data);
      setClasses(classesRes.data);
      setEvents(eventsRes.data);
      setNotifications(notifsRes.data);
      setSubscription(subRes.data);
      setAvailableClasses(availRes.data);
      setAllMyPayments(allPayRes.data);
      setStoreProducts(productsRes.data);
      axios.get(`${API}/public/store-combos`).then(r => setStoreCombos(r.data || [])).catch(() => {});
      // Fetch trial credit status (non-blocking)
      axios.get(`${API}/portal/trial-credit`, { withCredentials: true })
        .then(r => setTrialCredit(r.data || { has_credit: false, credit_value: 0 }))
        .catch(() => {});
      setRankingFem(rankFemRes.data?.players || []);
      setRankingMasc(rankMascRes.data?.players || []);
      if (memRes?.data) setMembership(memRes.data);
      setAvulsas(avulsasRes.data || []);
    } catch (e) {
      console.error('Error fetching portal data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, paymentId, attempts = 0) => {
    if (attempts >= 5) {
      setPollingPayment(false);
      toast.info('Verificação expirou. Verifique o status.');
      return;
    }
    try {
      const { data } = await axios.get(`${API}/stripe/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') {
        setPollingPayment(false);
        toast.success(t('portal.paymentConfirmed'));
        fetchData();
        setSearchParams({});
        return;
      } else if (data.status === 'expired') {
        setPollingPayment(false);
        toast.error(t('portal.errorGeneric'));
        setSearchParams({});
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, paymentId, attempts + 1), 2000);
    } catch {
      if (attempts < 4) setTimeout(() => pollPaymentStatus(sessionId, paymentId, attempts + 1), 2000);
      else { setPollingPayment(false); toast.error(t('portal.processError')); }
    }
  }, [fetchData, setSearchParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentId = searchParams.get('payment_id');
    const subscriptionResult = searchParams.get('subscription');
    
    if (sessionId && paymentId) {
      setPollingPayment(true);
      pollPaymentStatus(sessionId, paymentId);
    } else if (subscriptionResult === 'success' && sessionId) {
      // Poll subscription status
      setPollingPayment(true);
      const pollSub = async (attempts = 0) => {
        if (attempts >= 6) { setPollingPayment(false); setSearchParams({}); return; }
        try {
          const { data } = await axios.get(`${API}/subscriptions/status/${sessionId}`, { withCredentials: true });
          if (data.payment_status === 'paid') {
            setPollingPayment(false);
            toast.success(t('portal.subscriptionActivated'));
            fetchData();
            setSearchParams({});
            return;
          }
          setTimeout(() => pollSub(attempts + 1), 2000);
        } catch {
          setTimeout(() => pollSub(attempts + 1), 2000);
        }
      };
      pollSub();
    }
  }, [searchParams, pollPaymentStatus, fetchData, setSearchParams]);

  const handleSubscribe = async (planId) => {
    // Ask user for start day if today is not the 1st
    const todayDay = new Date().getDate();
    let startDay = todayDay;
    if (todayDay > 1) {
      const input = window.prompt(
        `Qual dia do mes voce vai comecar as aulas? (1-31)\n\nHoje e dia ${todayDay}. Sua cobranca mensal sera sempre no dia 01, mas se voce comecar hoje vamos cobrar uma fracao proporcional ao mes atual.\n\nDigite o dia (recomendado: ${todayDay}):`,
        String(todayDay)
      );
      if (input === null) return; // user cancelled
      const parsed = parseInt(input, 10);
      if (isNaN(parsed) || parsed < 1 || parsed > 31) {
        toast.error(t('portal.invalidDay'));
        return;
      }
      startDay = parsed;
    }
    setSubLoading(true);
    try {
      const originUrl = window.location.origin;
      const { data } = await axios.post(`${API}/subscriptions/checkout`, {
        plan_id: planId,
        origin_url: originUrl,
        start_day: startDay,
      }, { withCredentials: true });
      if (data.url) {
        const a = document.createElement('a');
        a.href = data.url;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      else { toast.error(t('portal.checkoutError')); setSubLoading(false); }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('portal.processError'));
      setSubLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar sua assinatura? Ela continuará ativa até o fim do período.')) return;
    try {
      const { data } = await axios.post(`${API}/subscriptions/cancel`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  const handleStripePayment = async (payment) => {
    setStripeLoading(payment.id);
    try {
      const originUrl = window.location.origin;
      const { data } = await axios.post(`${API}/portal/stripe-checkout`, {
        payment_id: payment.id,
        origin_url: originUrl
      }, { withCredentials: true });
      
      if (data.url) {
        // Safari-safe redirect: use anchor click for cross-origin navigation
        const a = document.createElement('a');
        a.href = data.url;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        toast.error('Erro: URL de pagamento nao recebida. Tente novamente.');
        setStripeLoading(null);
      }
    } catch (e) {
      console.error('Stripe checkout error:', e);
      toast.error(e.response?.data?.detail || 'Erro ao iniciar pagamento');
      setStripeLoading(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const pendingPayments = payments.filter(p => p.status !== 'pago');
  const paidPayments = payments.filter(p => p.status === 'pago');
  const upcomingClasses = classes.filter(c => c.date >= new Date().toISOString().split('T')[0]);
  const upcomingEvents = events.filter(e => e.date >= new Date().toISOString().split('T')[0]).slice(0, 5);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {checkoutRedirectUrl && (
        <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center px-6 text-center" data-testid="portal-checkout-overlay">
          <div className="w-14 h-14 rounded-full border-4 border-[#E85D04] border-t-transparent animate-spin mb-6" />
          <h2 className="font-['Outfit'] text-xl font-bold text-[#1A1A1A]">
            {i18n.language === 'en' ? 'Opening secure payment...' : 'Abrindo pagamento seguro...'}
          </h2>
          <p className="text-sm text-[#73706A] mt-2 max-w-sm">
            {i18n.language === 'en'
              ? "If the page doesn't open, tap the button below."
              : 'Se a página não abrir sozinha, toque no botão abaixo.'}
          </p>
          <a
            href={checkoutRedirectUrl}
            target="_self"
            rel="noopener"
            className="inline-flex items-center justify-center w-full max-w-sm bg-[#E85D04] hover:bg-[#DC2F02] text-white font-semibold py-4 rounded-lg text-lg mt-6"
            data-testid="portal-checkout-open-btn"
          >
            {i18n.language === 'en' ? 'Go to payment' : 'Ir para o pagamento'}
          </a>
          <button
            onClick={() => { navigator.clipboard?.writeText(checkoutRedirectUrl); toast.success(i18n.language === 'en' ? 'Link copied' : 'Link copiado'); }}
            className="mt-3 text-sm text-[#0077B6] underline"
            data-testid="portal-checkout-copy-btn"
          >
            {i18n.language === 'en' ? 'Copy payment link' : 'Copiar link do pagamento'}
          </button>
          <p className="text-xs text-[#73706A] mt-4 max-w-sm">
            {i18n.language === 'en'
              ? 'Using Instagram/Facebook? Open this site in Safari or Chrome to pay.'
              : 'Está pelo Instagram/Facebook? Abra este site no Safari ou Chrome para pagar.'}
          </p>
          <button
            onClick={cancelCheckout}
            className="mt-6 text-sm text-[#73706A]"
            data-testid="portal-checkout-cancel-btn"
          >
            {i18n.language === 'en' ? 'Cancel' : 'Cancelar'}
          </button>
        </div>
      )}
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-[#E6E2D8]">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display='none'; }} />
            <span className="font-['Outfit'] font-bold text-[#1A1A1A]">Tia Karol Sports</span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageToggle />
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-[#1A1A1A]">{user?.name}</p>
              <p className="text-xs text-[#73706A]">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg text-[#9B2226] hover:bg-red-50 transition-colors"
              data-testid="portal-logout-btn"
            >
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Polling Banner */}
        {pollingPayment && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-[#0077B6] border-t-transparent rounded-full animate-spin" />
            <p className="text-[#0077B6] font-medium">Verificando pagamento...</p>
          </div>
        )}

        {/* Welcome */}
        <div className="bg-white border border-[#E6E2D8] rounded-xl p-6" data-testid="portal-welcome">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#E85D04] rounded-full flex items-center justify-center">
              <User size={28} className="text-white" />
            </div>
            <div>
              <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">
                {t('portal.greeting', { name: profile?.student?.name || user?.name })}
              </h1>
              <p className="text-[#73706A]">
                {profile?.linked
                  ? `${profile.student.plan === '3x' ? '3x' : profile.student.plan === '2x' ? '2x' : '1x'}/${t('register.perWeek') === 'x per week' ? 'week' : 'semana'} · ${profile.student.category} · ${profile.student.status}`
                  : t('common.loading')}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs - Sidebar on mobile */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col sm:flex-row gap-4">
          <div className="sm:w-52 shrink-0">
            <TabsList className="flex flex-row sm:flex-col w-full overflow-x-auto sm:overflow-visible bg-white border border-[#E6E2D8] rounded-lg p-1.5 sm:p-2 gap-1">
              <TabsTrigger value="home" data-testid="portal-tab-home" className="sm:w-full sm:justify-start px-2.5 py-2 text-xs sm:text-sm whitespace-nowrap">
                <House size={16} className="mr-1 sm:mr-2" /> {t('portal.tabs.home')}
              </TabsTrigger>
              <TabsTrigger value="payments" data-testid="portal-tab-payments" className="sm:w-full sm:justify-start px-2.5 py-2 text-xs sm:text-sm whitespace-nowrap">
                <Money size={16} className="mr-1 sm:mr-2" /> {t('portal.tabs.payments')}
              </TabsTrigger>
              <TabsTrigger value="classes" data-testid="portal-tab-classes" className="sm:w-full sm:justify-start px-2.5 py-2 text-xs sm:text-sm whitespace-nowrap">
                <CalendarDots size={16} className="mr-1 sm:mr-2" /> {t('portal.tabs.classes')}
              </TabsTrigger>
              <TabsTrigger value="events" data-testid="portal-tab-events" className="sm:w-full sm:justify-start px-2.5 py-2 text-xs sm:text-sm whitespace-nowrap">
                <Trophy size={16} className="mr-1 sm:mr-2" /> {t('portal.tabs.events')}
              </TabsTrigger>
              <TabsTrigger value="league" data-testid="portal-tab-league" className="sm:w-full sm:justify-start px-2.5 py-2 text-xs sm:text-sm whitespace-nowrap">
                <Volleyball size={16} className="mr-1 sm:mr-2" /> {t('portal.tabs.league')}
              </TabsTrigger>
              <TabsTrigger value="rankings" data-testid="portal-tab-rankings" className="sm:w-full sm:justify-start px-2.5 py-2 text-xs sm:text-sm whitespace-nowrap">
                <Trophy size={16} className="mr-1 sm:mr-2" /> {t('portal.tabs.rankings')}
              </TabsTrigger>
              <TabsTrigger value="membership" data-testid="portal-tab-membership" className="sm:w-full sm:justify-start px-2.5 py-2 text-xs sm:text-sm whitespace-nowrap">
                <Volleyball size={16} className="mr-1 sm:mr-2" /> {t('portal.tabs.membership')}
              </TabsTrigger>
              <TabsTrigger value="avulsa" data-testid="portal-tab-avulsa" className="sm:w-full sm:justify-start px-2.5 py-2 text-xs sm:text-sm whitespace-nowrap">
                <Volleyball size={16} className="mr-1 sm:mr-2" /> {t('portal.tabs.avulsa')}
              </TabsTrigger>
              <TabsTrigger value="rental" data-testid="portal-tab-rental" className="sm:w-full sm:justify-start px-2.5 py-2 text-xs sm:text-sm whitespace-nowrap">
                <Volleyball size={16} className="mr-1 sm:mr-2" /> {t('portal.tabs.rental')}
              </TabsTrigger>
              <TabsTrigger value="store" data-testid="portal-tab-store" className="sm:w-full sm:justify-start px-2.5 py-2 text-xs sm:text-sm whitespace-nowrap">
                <TShirt size={16} className="mr-1 sm:mr-2" /> {t('portal.tabs.store')}
              </TabsTrigger>
              <TabsTrigger value="my-payments" data-testid="portal-tab-my-payments" className="sm:w-full sm:justify-start px-2.5 py-2 text-xs sm:text-sm whitespace-nowrap">
                <Wallet size={16} className="mr-1 sm:mr-2" /> {t('portal.tabs.myPayments')}
              </TabsTrigger>
              <TabsTrigger value="notifications" data-testid="portal-tab-notifications" className="sm:w-full sm:justify-start px-2.5 py-2 text-xs sm:text-sm whitespace-nowrap relative">
                <Bell size={16} className="mr-1 sm:mr-2" /> {t('portal.tabs.notifications')}
                {notifications.filter(n => !n.read_by_student).length > 0 && (
                  <span className="ml-auto bg-[#9B2226] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">
                    {notifications.filter(n => !n.read_by_student).length}
                  </span>
                )}
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="portal-tab-settings">
              <Lock size={16} className="mr-1 sm:mr-2" /> {t('portal.tabs.settings')}
            </TabsTrigger>
          </TabsList>
          </div>

          <div className="flex-1 min-w-0">
          {/* Home Tab */}
          <TabsContent value="home" className="space-y-6 mt-0">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white border border-[#E6E2D8] rounded-lg p-5 card-hover">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#9B2226] rounded-lg flex items-center justify-center">
                    <Warning size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-[#73706A]">{t('portal.statsPending')}</p>
                    <p className="text-xl font-bold text-[#1A1A1A] font-['Outfit']">{pendingPayments.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-[#E6E2D8] rounded-lg p-5 card-hover">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#E85D04] rounded-lg flex items-center justify-center">
                    <CalendarDots size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-[#73706A]">{t('portal.statsUpcomingClasses')}</p>
                    <p className="text-xl font-bold text-[#1A1A1A] font-['Outfit']">{upcomingClasses.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-[#E6E2D8] rounded-lg p-5 card-hover">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0077B6] rounded-lg flex items-center justify-center">
                    <Trophy size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-[#73706A]">{t('portal.statsEvents')}</p>
                    <p className="text-xl font-bold text-[#1A1A1A] font-['Outfit']">{upcomingEvents.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* My Turmas */}
            <MyTurmasCard />

            {/* Subscription Card */}
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-6" data-testid="subscription-card">
              <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
                <CreditCard size={20} className="text-[#E85D04]" />
                {t('portal.mySubscription')}
              </h2>
              {trialCredit.has_credit && (
                <div
                  className="mb-4 p-3 rounded-lg border-2 border-[#FFB703] bg-[#FFF3E6] text-sm text-[#1A1A1A]"
                  data-testid="trial-credit-banner"
                  dangerouslySetInnerHTML={{ __html: t('portal.trialCreditBanner') }}
                />
              )}
              {subscription?.has_subscription && subscription?.status === 'active' ? (
                <div>
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                    <CheckCircle size={24} className="text-[#2D6A4F]" weight="fill" />
                    <div>
                      <p className="font-medium text-[#2D6A4F]">{t('portal.subActiveTitle')}</p>
                      <p className="text-sm text-[#73706A]">
                        {t('portal.subActivePlan')} {subscription.plan === '3x' ? t('portal.subPlanWeek3') : subscription.plan === '2x' ? t('portal.subPlanWeek2') : t('portal.subPlanWeek1')}
                        {subscription.cancel_at_period_end && ' - ' + t('portal.subCancelScheduled')}
                      </p>
                    </div>
                  </div>
                  {!subscription.cancel_at_period_end && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelSubscription}
                      className="text-[#9B2226] border-[#9B2226] hover:bg-red-50"
                      data-testid="cancel-subscription-btn"
                    >
                      {t('portal.subCancelBtn')}
                    </Button>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-sm text-[#73706A] mb-4">
                    {t('portal.subDescription')}
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { id: '1x', label: t('portal.weeklyOptions1x'), price: '$90' },
                      { id: '2x', label: t('portal.weeklyOptions2x'), price: '$170' },
                      { id: '3x', label: t('portal.weeklyOptions3x'), price: '$200' }
                    ].map(plan => (
                      <button
                        key={plan.id}
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={subLoading}
                        className={`p-4 rounded-lg border text-center transition-all hover:border-[#E85D04] hover:shadow-sm ${
                          subscription?.plan === plan.id ? 'border-[#E85D04] bg-orange-50' : 'border-[#E6E2D8]'
                        }`}
                        data-testid={`subscribe-${plan.id}`}
                      >
                        <p className="font-semibold text-sm text-[#1A1A1A]">{plan.label}</p>
                        <p className="text-xl font-bold text-[#E85D04] font-['Outfit'] mt-1">{plan.price}</p>
                        <p className="text-[10px] text-[#73706A] mt-1">{t('portal.monthShort')}</p>
                      </button>
                    ))}
                  </div>
                  {subLoading && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-[#0077B6]">
                      <div className="w-4 h-4 border-2 border-[#0077B6] border-t-transparent rounded-full animate-spin" />
                      {t('portal.subRedirecting')}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Pending Payments Alert */}
            {pendingPayments.length > 0 && (
              <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
                <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
                  <CreditCard size={20} className="text-[#E85D04]" />
                  {t('portal.pendingSubscriptions')}
                </h2>
                <div className="space-y-3">
                  {pendingPayments.slice(0, 3).map((p, i) => (
                    <div key={p.id} className="flex items-center justify-between p-4 bg-[#F5F2EB] rounded-lg" data-testid={`pending-payment-${i}`}>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-[#1A1A1A]">{t('portal.ref')}: {p.reference_month}</p>
                          {p.is_advance && (
                            <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-[#0077B6] text-white" data-testid={`payment-advance-badge-${i}`}>
                              {t('portal.advance')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#73706A]">
                          {p.is_advance ? t('portal.advanceMsg') : p.payment_date}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-[#E85D04]">
                          $ {p.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                        <Button
                          size="sm"
                          className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                          onClick={() => handleStripePayment(p)}
                          disabled={stripeLoading === p.id}
                          data-testid={`pay-btn-${i}`}
                        >
                          {stripeLoading === p.id ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <CreditCard size={16} className="mr-1" /> {t('common.card')}
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white"
                          onClick={() => {
                            navigator.clipboard.writeText('5618596830');
                            toast.success(t('portal.zelleNumberCopied', { amount: p.amount.toFixed(2) }));
                          }}
                          data-testid={`zelle-btn-${i}`}
                        >
                          <Phone size={16} className="mr-1" /> Zelle
                        </Button>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                            className="hidden"
                            onChange={(e) => handleZelleUpload(p.id, e.target.files[0])}
                            data-testid={`zelle-upload-input-home-${i}`}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white pointer-events-none"
                            disabled={uploadingZelle === p.id}
                            data-testid={`zelle-upload-btn-home-${i}`}
                          >
                            {uploadingZelle === p.id ? (
                              <div className="w-4 h-4 border-2 border-[#2D6A4F] border-t-transparent rounded-full animate-spin mr-1" />
                            ) : (
                              <UploadSimple size={16} className="mr-1" />
                            )}
                            {t('portal.uniformReceipt')}
                          </Button>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
                {pendingPayments.length > 3 && (
                  <button onClick={() => setActiveTab('payments')} className="mt-3 text-sm text-[#E85D04] hover:text-[#DC2F02] flex items-center gap-1">
                    {t('portal.viewAll')} <ArrowRight size={14} />
                  </button>
                )}
              </div>
            )}

            {/* Upcoming Classes */}
            {upcomingClasses.length > 0 && (
              <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
                <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4">{t('portal.upcomingClassesTitle')}</h2>
                <div className="space-y-3">
                  {upcomingClasses.slice(0, 3).map((c, i) => (
                    <div key={c.id} className="flex items-center justify-between p-4 bg-[#F5F2EB] rounded-lg" data-testid={`upcoming-class-${i}`}>
                      <div>
                        <p className="font-medium text-[#1A1A1A]">{c.title}</p>
                        <p className="text-sm text-[#73706A]">{c.instructor}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-[#1A1A1A]">{c.date}</p>
                        <p className="text-sm text-[#0077B6] flex items-center gap-1 justify-end">
                          <Clock size={14} /> {c.start_time} - {c.end_time}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-6 mt-0">
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
              <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4">{i18n.language === 'en' ? 'My Subscription Payments' : 'Minhas Mensalidades'}</h2>
              {payments.length === 0 ? (
                <p className="text-[#73706A] text-center py-8">{i18n.language === 'en' ? 'No subscription found' : 'Nenhuma mensalidade encontrada'}</p>
              ) : (
                <div className="space-y-3">
                  {payments.map((p, i) => (
                    <div key={p.id} className="flex items-center justify-between p-4 bg-[#F5F2EB] rounded-lg" data-testid={`portal-payment-${i}`}>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-[#1A1A1A]">Ref: {p.reference_month}</p>
                          {p.is_advance && p.status !== 'pago' && (
                            <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-[#0077B6] text-white">
                              Antecipada
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#73706A]">{p.payment_method} - {p.payment_date}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-[#1A1A1A]">
                          $ {p.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                        {p.status === 'pago' ? (
                          <div className="flex items-center gap-2">
                            <span className="badge badge-success flex items-center gap-1">
                              <CheckCircle size={12} weight="fill" /> Pago
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                try {
                                  generatePaymentReceipt({
                                    ...p,
                                    student_name: profile?.student?.name || user?.name
                                  });
                                  toast.success(t('portal.copied'));
                                } catch (err) {
                                  toast.error('Erro ao gerar comprovante');
                                }
                              }}
                              className="text-[#2D6A4F] hover:text-[#1B4332] hover:bg-green-50 p-1 h-auto"
                              data-testid={`portal-receipt-btn-${i}`}
                              title={t('portal.downloadReceipt')}
                            >
                              <FilePdf size={18} />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                              onClick={() => handleStripePayment(p)}
                              disabled={stripeLoading === p.id}
                              data-testid={`portal-pay-btn-${i}`}
                            >
                              {stripeLoading === p.id ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <>
                                  <CreditCard size={16} className="mr-1" /> Cartao
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white"
                              onClick={() => {
                                navigator.clipboard.writeText('5618596830');
                                toast.success('Numero Zelle copiado! Envie $' + p.amount.toFixed(2) + ' para 5618596830');
                              }}
                              data-testid={`portal-zelle-btn-${i}`}
                            >
                              <Phone size={16} className="mr-1" /> Zelle
                            </Button>
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                className="hidden"
                                onChange={(e) => handleZelleUpload(p.id, e.target.files[0])}
                                data-testid={`portal-zelle-upload-input-${i}`}
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white pointer-events-none"
                                disabled={uploadingZelle === p.id}
                                data-testid={`portal-zelle-upload-btn-${i}`}
                              >
                                {uploadingZelle === p.id ? (
                                  <div className="w-4 h-4 border-2 border-[#2D6A4F] border-t-transparent rounded-full animate-spin mr-1" />
                                ) : (
                                  <UploadSimple size={16} className="mr-1" />
                                )}
                                Comprovante
                              </Button>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Classes Tab */}
          <TabsContent value="classes" className="space-y-6 mt-0">
            {/* My Enrolled Classes */}
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
              <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4">Minhas Aulas</h2>
              {classes.length === 0 ? (
                <p className="text-[#73706A] text-center py-4">Você ainda não está inscrito em nenhuma aula. Escolha abaixo!</p>
              ) : (
                <div className="space-y-3">
                  {classes.map((c, i) => (
                    <div key={c.id} className="p-4 bg-green-50 border border-green-200 rounded-lg" data-testid={`portal-class-${i}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <CheckCircle size={16} className="text-[#2D6A4F]" weight="fill" />
                            <h3 className="font-medium text-[#1A1A1A]">{c.title}</h3>
                          </div>
                          <p className="text-sm text-[#73706A] mt-1">{c.instructor}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="badge badge-info capitalize">{c.category}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#9B2226] hover:bg-red-50"
                            onClick={async () => {
                              if (!window.confirm('Cancelar inscrição nesta aula?')) return;
                              try {
                                await axios.post(`${API}/portal/unenroll/${c.id}`, {}, { withCredentials: true });
                                toast.success('Inscrição cancelada');
                                fetchData();
                              } catch (e) { toast.error('Erro ao cancelar'); }
                            }}
                            data-testid={`unenroll-${i}`}
                          >
                            Sair
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-sm">
                        <span className="flex items-center gap-1 text-[#0077B6]">
                          <CalendarDots size={16} /> {c.date}
                        </span>
                        <span className="flex items-center gap-1 text-[#73706A]">
                          <Clock size={16} /> {c.start_time} - {c.end_time}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Available Classes */}
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
              <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4">Aulas Disponíveis</h2>
              {availableClasses.filter(c => !c.is_enrolled).length === 0 ? (
                <p className="text-[#73706A] text-center py-4">{i18n.language === 'en' ? 'No classes available right now' : 'Nenhuma aula disponível no momento'}</p>
              ) : (
                <div className="space-y-3">
                  {availableClasses.filter(c => !c.is_enrolled).map((c, i) => (
                    <div key={c.id} className="p-4 bg-[#F5F2EB] rounded-lg border border-transparent hover:border-[#E85D04] transition-colors" data-testid={`available-class-${i}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-[#1A1A1A]">{c.title}</h3>
                          <p className="text-sm text-[#73706A] mt-1">{c.instructor}</p>
                          <div className="flex items-center gap-4 mt-3 text-sm">
                            <span className="flex items-center gap-1 text-[#0077B6]">
                              <CalendarDots size={16} /> {c.date}
                            </span>
                            <span className="flex items-center gap-1 text-[#73706A]">
                              <Clock size={16} /> {c.start_time} - {c.end_time}
                            </span>
                            <span className="badge badge-info capitalize">{c.category}</span>
                            <span className="text-[#73706A]">
                              {c.enrolled_count}/{c.max_students} vagas
                            </span>
                          </div>
                          {c.notes && <p className="text-xs text-[#73706A] mt-2 italic">{c.notes}</p>}
                        </div>
                        <Button
                          size="sm"
                          disabled={c.is_full || enrolling === c.id}
                          className={c.is_full ? "bg-[#73706A]" : "bg-[#E85D04] hover:bg-[#DC2F02] text-white"}
                          onClick={async () => {
                            setEnrolling(c.id);
                            try {
                              const { data } = await axios.post(`${API}/portal/enroll/${c.id}`, {}, { withCredentials: true });
                              toast.success(data.message);
                              fetchData();
                            } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao inscrever'); }
                            setEnrolling(null);
                          }}
                          data-testid={`enroll-${i}`}
                        >
                          {enrolling === c.id ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : c.is_full ? 'Lotada' : 'Inscrever-se'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-6 mt-0">
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
              <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4">{t('portal.events')}</h2>
              {events.length === 0 ? (
                <p className="text-[#73706A] text-center py-8">{t('portal.eventsEmpty')}</p>
              ) : (
                <div className="space-y-3">
                  {events.map((e, i) => (
                    <div key={e.id} className="p-4 bg-[#F5F2EB] rounded-lg" data-testid={`portal-event-${i}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <span className="badge badge-info capitalize text-xs">{e.event_type}</span>
                          <h3 className="font-medium text-[#1A1A1A] mt-1">{e.title}</h3>
                          {e.description && <p className="text-sm text-[#73706A] mt-1">{e.description}</p>}
                        </div>
                        <Button
                          size="sm"
                          className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                          onClick={() => setActiveTab('league')}
                          data-testid={`event-enroll-btn-${i}`}
                        >
                          <Trophy size={16} className="mr-1" /> Inscrever-se
                        </Button>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-sm">
                        <span className="flex items-center gap-1 text-[#0077B6]">
                          <CalendarDots size={16} /> {e.date}
                        </span>
                        {e.start_time && (
                          <span className="flex items-center gap-1 text-[#73706A]">
                            <Clock size={16} /> {e.start_time}{e.end_time && ` - ${e.end_time}`}
                          </span>
                        )}
                        {e.location && (
                          <span className="flex items-center gap-1 text-[#73706A]">
                            <MapPin size={16} /> {e.location}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* TK League Tab */}
          <TabsContent value="league" className="space-y-6 mt-0">
            <PortalDuplasConfirmadas />
            {leagueSuccess ? (
              <div className="bg-white border border-[#E6E2D8] rounded-lg p-8 text-center">
                <div className="w-20 h-20 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={40} className="text-white" weight="fill" />
                </div>
                <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A] mb-2">{t('common.confirmed')}</h2>
                <p className="text-[#73706A] mb-4">Sua inscricao na TK League foi confirmada com sucesso.</p>
                <Button onClick={() => { setLeagueSuccess(false); setLeagueCategories([]); setLeagueNotes(''); setLeagueStep('form'); }} className="bg-[#E85D04] hover:bg-[#DC2F02] text-white" data-testid="league-new-btn">
                  {t('common.newRegistration')}
                </Button>
              </div>
            ) : leagueStep === 'zelle-upload' ? (
              <div className="bg-white border border-[#E6E2D8] rounded-lg p-8 text-center space-y-6">
                <div className="w-20 h-20 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto">
                  <Phone size={40} className="text-white" />
                </div>
                <div>
                  <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">Pague via Zelle</h2>
                  <p className="text-[#73706A] mt-2">Envie <strong className="text-[#E85D04]">$40.00</strong> via Zelle para:</p>
                </div>
                <div className="bg-[#F5F2EB] rounded-lg p-6">
                  <p className="text-3xl font-bold text-[#1A1A1A] font-['Outfit'] tracking-wider">5618596830</p>
                  <p className="text-sm text-[#73706A] mt-2">Tia Karol Sports</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
                  <p className="text-sm text-yellow-800"><strong>Importante:</strong> Apos enviar o Zelle, envie o comprovante abaixo para confirmar automaticamente!</p>
                </div>
                <label className="cursor-pointer block">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    className="hidden"
                    onChange={(e) => handleLeagueReceiptUpload(e.target.files[0])}
                    data-testid="portal-league-zelle-upload"
                  />
                  <Button
                    className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white pointer-events-none"
                    disabled={uploadingLeagueReceipt}
                  >
                    {uploadingLeagueReceipt ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <UploadSimple size={20} className="mr-2" />
                    )}
                    {uploadingLeagueReceipt ? t('common.sending') : t('portal.uploadReceiptZelle')}
                  </Button>
                </label>
                <Button variant="outline" onClick={() => { setLeagueStep('form'); setLeagueSuccess(true); }} className="w-full">
                  Ja enviei, confirmar depois
                </Button>
              </div>
            ) : (
              <div className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden">
                <div className="h-32 bg-cover bg-center relative" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1541819616892-71c1f5112fa5?q=80&w=2940&auto=format&fit=crop)' }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20" />
                  <div className="absolute bottom-0 left-0 p-4 text-white flex items-center gap-2">
                    <Trophy size={24} className="text-[#FFB703]" weight="fill" />
                    <h2 className="font-['Outfit'] text-xl font-bold">TK League / Rei e Rainha</h2>
                  </div>
                </div>
                <div className="p-6 space-y-5">
                  <div>
                    <Label className="mb-3 block font-medium">Categorias *</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { value: 'misto', label: 'Misto' },
                        { value: 'open', label: 'Open' },
                        { value: 'feminino', label: 'Feminino' },
                        { value: 'rei_rainha', label: 'Rei e Rainha' }
                      ].map(cat => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => setLeagueCategories(prev => prev.includes(cat.value) ? prev.filter(c => c !== cat.value) : [...prev, cat.value])}
                          className={`p-3 rounded-lg border text-center font-medium transition-all ${leagueCategories.includes(cat.value) ? 'bg-[#E85D04] text-white border-[#E85D04]' : 'border-[#E6E2D8] hover:border-[#E85D04]'}`}
                          data-testid={`portal-league-cat-${cat.value}`}
                        >
                          <Trophy size={18} className={`mx-auto mb-1 ${leagueCategories.includes(cat.value) ? 'text-white' : 'text-[#E85D04]'}`} weight={leagueCategories.includes(cat.value) ? 'fill' : 'regular'} />
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>Observações</Label>
                    <textarea value={leagueNotes} onChange={(e) => setLeagueNotes(e.target.value)} className="input-field min-h-[60px] resize-none mt-1" placeholder="Alguma informação adicional?" data-testid="portal-league-notes" />
                  </div>
                  <div className="p-4 bg-[#F5F2EB] rounded-lg text-center" data-testid="portal-league-fee-box">
                    <p className="text-sm text-[#73706A]">{t('common.tax')}</p>
                    <p className="text-2xl font-bold text-[#E85D04] font-['Outfit']">${(40 * Math.max(1, leagueCategories.length)).toFixed(2)}</p>
                    <p className="text-[11px] text-[#73706A]">
                      {Math.max(1, leagueCategories.length)} categoria{leagueCategories.length > 1 ? 's' : ''} x $40
                    </p>
                    <p className="text-[10px] text-[#73706A] mt-1">
                      Cartao: ${(40 * Math.max(1, leagueCategories.length) * 1.05).toFixed(2)} (+ 5% taxa) | Zelle: ${(40 * Math.max(1, leagueCategories.length)).toFixed(2)}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      disabled={leagueSaving || leagueCategories.length === 0}
                      className="bg-[#E85D04] hover:bg-[#DC2F02] text-white py-3"
                      onClick={async () => {
                        setLeagueSaving(true);
                        try {
                          const { data: regData } = await axios.post(`${API}/league/register`, { name: profile?.student?.name || user?.name, email: user?.email, phone: profile?.student?.phone || user?.phone || '', categories: leagueCategories, notes: leagueNotes });
                          const originUrl = window.location.origin;
                          const { data: checkoutData } = await axios.post(`${API}/league/checkout`, { registration_id: regData.id, origin_url: originUrl });
                          if (checkoutData.url) window.location.href = checkoutData.url;
                        } catch (e) { toast.error(e.response?.data?.detail || t('portal.errorGeneric')); setLeagueSaving(false); }
                      }}
                      data-testid="portal-league-card-btn"
                    >
                      {leagueSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CreditCard size={18} className="mr-2" /> Cartao</>}
                    </Button>
                    <Button
                      variant="outline"
                      disabled={leagueSaving || leagueCategories.length === 0}
                      className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white py-3"
                      onClick={async () => {
                        setLeagueSaving(true);
                        try {
                          const total = 40 * Math.max(1, leagueCategories.length);
                          const { data } = await axios.post(`${API}/league/register`, { name: profile?.student?.name || user?.name, email: user?.email, phone: profile?.student?.phone || user?.phone || '', categories: leagueCategories, notes: (leagueNotes ? leagueNotes + ' | ' : '') + 'Pagamento via Zelle' });
                          setLeagueRegId(data.id);
                          navigator.clipboard.writeText('5618596830');
                          toast.success(t('portal.zelleNumberCopied', { amount: total.toFixed(2) }));
                          setLeagueStep('zelle-upload');
                        } catch (e) { toast.error(e.response?.data?.detail || t('portal.errorGeneric')); }
                        setLeagueSaving(false);
                      }}
                      data-testid="portal-league-zelle-btn"
                    >
                      <Phone size={18} className="mr-2" /> Zelle
                    </Button>
                  </div>
                  <p className="text-xs text-[#73706A] text-center">Zelle: envie ${(40 * Math.max(1, leagueCategories.length)).toFixed(2)} para <strong>5618596830</strong> (Tia Karol Sports)</p>
                </div>
              </div>
            )}

            {/* ============ Uniforme TK League $35 ============ */}
            <div className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden" data-testid="portal-league-uniform-section">
              <div className="h-20 bg-gradient-to-br from-[#9B2226] to-[#E85D04] relative">
                <div className="absolute inset-0 bg-black/20" />
                <div className="absolute bottom-0 left-0 p-4 text-white flex items-center gap-2">
                  <TShirt size={22} weight="fill" />
                  <h2 className="font-['Outfit'] text-lg font-bold">Uniforme TK League</h2>
                </div>
              </div>

              {uniformStep === 'success' ? (
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle size={32} className="text-white" weight="fill" />
                  </div>
                  <h3 className="font-['Outfit'] text-lg font-bold text-[#1A1A1A] mb-1">Pedido Confirmado!</h3>
                  <p className="text-[#73706A] text-sm mb-4">Em breve combinamos a entrega.</p>
                  <Button
                    onClick={() => { setUniformStep('form'); setUniformSize('M'); setUniformOrderId(null); }}
                    className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                    data-testid="portal-uniform-new-btn"
                  >
                    Novo Pedido
                  </Button>
                </div>
              ) : uniformStep === 'zelle-upload' ? (
                <div className="p-6 space-y-4 text-center">
                  <div className="w-16 h-16 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto">
                    <Phone size={32} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-['Outfit'] text-lg font-bold">Pague $35.00 via Zelle</h3>
                    <p className="text-sm text-[#73706A] mt-1">Zelle <strong>5618596830</strong> (Tia Karol Sports)</p>
                  </div>
                  <label className="cursor-pointer block">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      className="hidden"
                      onChange={(e) => handleUniformReceiptUpload(e.target.files[0])}
                      data-testid="portal-uniform-zelle-upload"
                    />
                    <Button className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white pointer-events-none" disabled={uploadingUniformReceipt}>
                      {uploadingUniformReceipt ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <UploadSimple size={18} className="mr-2" />}
                      {uploadingUniformReceipt ? t('common.sending') : t('portal.uploadReceiptZelle')}
                    </Button>
                  </label>
                  <Button variant="outline" onClick={() => setUniformStep('form')} className="w-full">
                    Ja enviei, confirmar depois
                  </Button>
                </div>
              ) : (
                <div className="p-6 space-y-4">
                  <div>
                    <Label className="mb-2 block font-medium">{i18n.language === 'en' ? 'Size' : 'Tamanho'}</Label>
                    <div className="grid grid-cols-6 gap-2">
                      {['PP', 'P', 'M', 'G', 'GG', 'XG'].map(s => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setUniformSize(s)}
                          className={`py-2 rounded-lg border font-medium text-sm transition-all ${uniformSize === s ? 'bg-[#E85D04] text-white border-[#E85D04]' : 'border-[#E6E2D8] hover:border-[#E85D04]'}`}
                          data-testid={`portal-uniform-size-${s}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 bg-[#F5F2EB] rounded-lg text-center">
                    <p className="text-sm text-[#73706A]">Uniforme Oficial da Liga</p>
                    <p className="text-2xl font-bold text-[#E85D04] font-['Outfit']">$35.00</p>
                    <p className="text-[10px] text-[#73706A]">Cartao: $36.75 (+ 5% taxa) | Zelle: $35.00</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      disabled={uniformSaving}
                      onClick={handleUniformCard}
                      className="bg-[#E85D04] hover:bg-[#DC2F02] text-white py-3"
                      data-testid="portal-uniform-card-btn"
                    >
                      {uniformSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CreditCard size={18} className="mr-2" /> Cartao</>}
                    </Button>
                    <Button
                      variant="outline"
                      disabled={uniformSaving}
                      onClick={handleUniformZelle}
                      className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white py-3"
                      data-testid="portal-uniform-zelle-btn"
                    >
                      <Phone size={18} className="mr-2" /> Zelle
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Rankings Tab */}
          <TabsContent value="rankings" className="space-y-6 mt-0">
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-4 sm:p-6">
              <h2 className="font-['Outfit'] font-bold text-xl text-[#1A1A1A] mb-1 flex items-center gap-2">
                <Trophy size={22} className="text-[#E85D04]" weight="fill" /> Ranking TK League
              </h2>
              <p className="text-sm text-[#73706A] mb-4">Classificacao oficial por etapas. Atualizado pela organizacao.</p>

              <Tabs defaultValue="rank-fem">
                <TabsList className="bg-[#F5F2EB] mb-4">
                  <TabsTrigger value="rank-fem" className="px-4" data-testid="portal-tab-rank-fem">Feminino</TabsTrigger>
                  <TabsTrigger value="rank-masc" className="px-4" data-testid="portal-tab-rank-masc">Masculino</TabsTrigger>
                </TabsList>
                <TabsContent value="rank-fem">
                  <RankingTable rows={rankingFem} genderLabel="Feminino" crownLabel="Rainha" />
                </TabsContent>
                <TabsContent value="rank-masc">
                  <RankingTable rows={rankingMasc} genderLabel="Masculino" crownLabel="Rei" />
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>

          {/* Membership Open Play Tab */}
          <TabsContent value="membership" className="space-y-6 mt-0">
            <div className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden" data-testid="portal-membership-card">
              <div className="h-24 bg-gradient-to-br from-[#0077B6] to-[#03045E] relative">
                <div className="absolute inset-0 bg-black/20" />
                <div className="absolute bottom-0 left-0 p-4 text-white flex items-center gap-2">
                  <Volleyball size={26} weight="fill" />
                  <h2 className="font-['Outfit'] text-xl font-bold">{membership.label}</h2>
                </div>
              </div>

              {membershipStep === 'success' ? (
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle size={32} className="text-white" weight="fill" />
                  </div>
                  <h3 className="font-['Outfit'] text-lg font-bold text-[#1A1A1A] mb-1">Membership Confirmada!</h3>
                  <p className="text-[#73706A] text-sm mb-4">Voce esta liberada para os Open Plays do mes.</p>
                  <Button onClick={() => { setMembershipStep('idle'); setMembershipOrderId(null); }} variant="outline" data-testid="portal-membership-new">
                    {t('common.back')}
                  </Button>
                </div>
              ) : membershipStep === 'zelle-upload' ? (
                <div className="p-6 space-y-4 text-center">
                  <div className="w-16 h-16 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto">
                    <Phone size={32} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-['Outfit'] text-lg font-bold">Pague ${(membership.price || 0).toFixed(2)} via Zelle</h3>
                    <p className="text-sm text-[#73706A] mt-1">Zelle <strong>5618596830</strong> (Tia Karol Sports)</p>
                  </div>
                  <label className="cursor-pointer block">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file || !membershipOrderId) return;
                        setUploadingMembershipReceipt(true);
                        try {
                          const fd = new FormData();
                          fd.append('file', file);
                          // Reuse generic zelle-receipt endpoint - upload to membership orders
                          await axios.post(`${API}/zelle-receipt/upload?order_id=${membershipOrderId}&type=membership_open_play`, fd, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          toast.success(t('common.receiptSent'));
                          setMembershipStep('success');
                          fetchData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Erro ao enviar comprovante');
                        } finally {
                          setUploadingMembershipReceipt(false);
                        }
                      }}
                      data-testid="portal-membership-zelle-upload"
                    />
                    <Button className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white pointer-events-none" disabled={uploadingMembershipReceipt}>
                      {uploadingMembershipReceipt ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <UploadSimple size={18} className="mr-2" />}
                      {uploadingMembershipReceipt ? t('common.sending') : t('portal.uploadReceiptZelle')}
                    </Button>
                  </label>
                  <Button variant="outline" onClick={() => setMembershipStep('idle')} className="w-full">
                    Cancelar
                  </Button>
                </div>
              ) : (
                <div className="p-6 space-y-4">
                  <p className="text-[#73706A] text-sm">{membership.description || 'Acesso aos Open Plays mensais'}</p>
                  <div className="p-5 bg-gradient-to-br from-[#F5F2EB] to-[#FDFBF7] rounded-xl text-center">
                    <p className="text-xs text-[#73706A] uppercase tracking-wide">{i18n.language === 'en' ? 'Monthly amount' : 'Valor mensal'}</p>
                    <p className="text-4xl font-bold text-[#0077B6] font-['Outfit'] mt-1">${(membership.price || 0).toFixed(2)}</p>
                    <p className="text-[10px] text-[#73706A] mt-1">Cartao: ${(membership.price * 1.05).toFixed(2)} (+ 5% taxa) | Zelle: ${(membership.price || 0).toFixed(2)}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      disabled={membershipSaving}
                      onClick={handleMembershipCard}
                      className="bg-[#0077B6] hover:bg-[#03045E] text-white py-3"
                      data-testid="portal-membership-card-btn"
                    >
                      {membershipSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CreditCard size={18} className="mr-2" /> Cartao</>}
                    </Button>
                    <Button
                      variant="outline"
                      disabled={membershipSaving}
                      onClick={handleMembershipZelle}
                      className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white py-3"
                      data-testid="portal-membership-zelle-btn"
                    >
                      <Phone size={18} className="mr-2" /> Zelle
                    </Button>
                  </div>

                  <div className="border-t border-[#E6E2D8] pt-4">
                    <p className="text-sm font-semibold text-[#1A1A1A] mb-1">Prefere pagar só o dia?</p>
                    <p className="text-xs text-[#73706A] mb-3">Pague apenas o Open Play de hoje por <span className="font-bold text-[#E85D04]">$12</span> (aluno), sem assinar o plano mensal.</p>
                    <label className="block text-xs font-semibold text-[#73706A] mb-1">Dia do Open Play</label>
                    <input type="date" value={dayPassDate} onChange={(e) => setDayPassDate(e.target.value)} className="w-full border border-[#E6E2D8] rounded-lg px-3 py-2 text-sm mb-3" data-testid="portal-openplay-date" />
                    <Button disabled={dayPassSaving} onClick={handleOpenPlayDay} className="w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white py-3" data-testid="portal-openplay-day-btn">
                      {dayPassSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CreditCard size={18} className="mr-2" /> Pagar o dia $12 (cartão)</>}
                    </Button>
                    <Button onClick={handleOpenPlayDayZelle} variant="outline" className="w-full mt-2 border-[#0077B6] text-[#0077B6] py-3" data-testid="portal-openplay-day-zelle-btn">
                      <Phone size={18} className="mr-2" /> Pagar o dia $12 (Zelle)
                    </Button>
                    {dayPassRegId && (
                      <div className="mt-3 bg-[#F0F7FF] border border-[#0077B6]/30 rounded-lg p-3">
                        <p className="text-xs text-[#1A1A1A] font-semibold mb-2">Envie o comprovante do Zelle:</p>
                        <input type="file" accept="image/*,application/pdf" onChange={onOpenPlayZelleFile} className="text-xs w-full" data-testid="portal-openplay-zelle-input" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {portalEvents.length > 0 && (
                <div className="bg-white border border-[#E6E2D8] rounded-lg p-6 mt-6" data-testid="portal-events-section">
                  <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-1 flex items-center gap-2">
                    <CalendarDots size={20} className="text-[#E85D04]" /> Eventos
                  </h2>
                  <p className="text-sm text-[#73706A] mb-4">Pague eventos e atividades especiais da escola.</p>
                  <div className="space-y-3">
                    {portalEvents.map((ev, i) => (
                      <div key={ev.id} className="flex items-center justify-between gap-3 border border-[#E6E2D8] rounded-lg p-4" data-testid={`portal-event-${i}`}>
                        <div>
                          <p className="font-semibold text-[#1A1A1A]">{ev.name}</p>
                          {ev.description && <p className="text-xs text-[#73706A]">{ev.description}</p>}
                          <p className="text-sm text-[#2D6A4F] font-semibold mt-1">Aluno ${ev.price_student?.toFixed(2)}</p>
                          {ev.closes_on && <p className={`text-[11px] font-semibold ${ev.closed ? 'text-[#9B2226]' : 'text-[#73706A]'}`}>{ev.closed ? 'Inscrições encerradas' : `Inscrições até ${new Date(ev.closes_on + 'T00:00:00').toLocaleDateString('pt-BR')}`}</p>}
                        </div>
                        {!ev.closed && <Button onClick={() => navigate(`/evento/${ev.id}?nome=${encodeURIComponent(profile?.student?.name || user?.name || '')}&tipo=aluno`)} className="bg-[#E85D04] hover:bg-[#DC2F02] text-white flex-shrink-0" data-testid={`portal-event-pay-${i}`}>
                          Pagar
                        </Button>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Aula Avulsa Tab */}
          <TabsContent value="avulsa" className="space-y-6 mt-0">
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-6" data-testid="avulsa-section">
              <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-1 flex items-center gap-2">
                <Volleyball size={20} className="text-[#E85D04]" /> Aulas Avulsas
              </h2>
              <p className="text-sm text-[#73706A] mb-4">
                Reserve uma aula avulsa, jogo treino ou aula feminina sem precisar de plano mensal.
              </p>

              {avulsaStep === 'success' ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center" data-testid="avulsa-success">
                  <CheckCircle size={48} className="text-[#2D6A4F] mx-auto mb-3" weight="fill" />
                  <p className="font-semibold text-[#2D6A4F] text-lg">{t('common.confirmed')}</p>
                  <p className="text-sm text-[#73706A] mt-2">
                    Voce vai receber um email de confirmacao. Te esperamos!
                  </p>
                  <Button
                    onClick={() => { setAvulsaStep('idle'); setAvulsaRegId(null); setAvulsaDates({ avulsa: '', fem: '', jogo: '' }); }}
                    className="mt-4 bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                    data-testid="avulsa-new-btn"
                  >
                    Nova inscricao
                  </Button>
                </div>
              ) : avulsaStep === 'zelle-upload' ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6" data-testid="avulsa-zelle-upload">
                  <h3 className="font-semibold text-[#0077B6] mb-2 flex items-center gap-2">
                    <Phone size={20} /> Pague via Zelle
                  </h3>
                  <p className="text-sm text-[#1A1A1A] mb-3">
                    Envie <strong>${(AVULSA_TYPES[avulsaRegId?.type]?.price || 0).toFixed(2)}</strong> pra o numero abaixo e anexe o comprovante:
                  </p>
                  <div className="bg-white border border-[#E6E2D8] rounded p-3 mb-4 flex items-center justify-between">
                    <span className="font-mono text-lg font-bold text-[#1A1A1A]">5618596830</span>
                    <button
                      onClick={() => { navigator.clipboard.writeText('5618596830'); toast.success(t('portal.copied')); }}
                      className="text-[#0077B6] hover:text-[#03045E]"
                    >
                      <Copy size={20} />
                    </button>
                  </div>
                  <label className="block">
                    <span className="text-sm font-medium text-[#1A1A1A]">{t('portal.uniformReceipt')} (PDF/JPG/PNG) *</span>
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,image/gif,image/webp"
                      onChange={(e) => handleAvulsaReceiptUpload(e.target.files?.[0])}
                      disabled={uploadingAvulsaReceipt}
                      className="mt-1 block w-full text-sm border border-[#E6E2D8] rounded-lg p-2 file:mr-2 file:px-3 file:py-1 file:rounded file:border-0 file:bg-[#E85D04] file:text-white"
                      data-testid="avulsa-zelle-file-input"
                    />
                  </label>
                  {uploadingAvulsaReceipt && <p className="text-sm text-[#73706A] mt-2">{t('common.sending')}</p>}
                  <button
                    onClick={() => { setAvulsaStep('idle'); setAvulsaRegId(null); }}
                    className="mt-3 text-sm text-[#73706A] hover:text-[#1A1A1A]"
                  >
                    Cancelar e voltar
                  </button>
                </div>
              ) : (
                <>
                  {/* 3 cards: Avulsa $25, Fem $20, Jogo Treino $25 - cada um com seu date picker */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {Object.entries(AVULSA_TYPES).map(([key, cfg]) => {
                      const date = avulsaDates[key];
                      const dateError = date ? validateAvulsaDate(key, date) : null;
                      const quickDates = cfg.allowedDay !== null ? getNextWeekdays(cfg.allowedDay, 4, 2) : [];
                      return (
                        <div
                          key={key}
                          className="border-2 border-[#E6E2D8] rounded-lg p-5 flex flex-col"
                          style={{ borderTopColor: cfg.color, borderTopWidth: 4 }}
                          data-testid={`avulsa-card-${key}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Volleyball size={20} style={{ color: cfg.color }} weight="fill" />
                            <h3 className="font-['Outfit'] font-semibold text-base text-[#1A1A1A]">{cfg.label}</h3>
                          </div>
                          <p className="text-3xl font-bold font-['Outfit'] mb-1" style={{ color: cfg.color }}>
                            ${cfg.price.toFixed(2)}
                          </p>
                          <p className="text-xs text-[#73706A] mb-3">
                            Cartao: ${cfg.cardPrice.toFixed(2)} (taxa 5%)
                          </p>

                          {/* Per-card date input */}
                          <div className="mb-2">
                            <Label className="text-xs text-[#73706A] uppercase font-medium">
                              Data {cfg.allowedDay !== null && <span className="normal-case text-[10px]">(somente {cfg.dayName}s)</span>}
                            </Label>
                            <input
                              type="date"
                              value={date}
                              onChange={(e) => setAvulsaDates({ ...avulsaDates, [key]: e.target.value })}
                              className={`mt-1 block w-full border rounded-lg p-2 text-sm focus:outline-none ${dateError ? 'border-[#9B2226]' : 'border-[#E6E2D8] focus:border-[#E85D04]'}`}
                              data-testid={`avulsa-date-${key}`}
                            />
                            {dateError && <p className="text-[10px] text-[#9B2226] mt-1" data-testid={`avulsa-date-error-${key}`}>{dateError}</p>}
                          </div>

                          {/* Quick-select chips for fem/jogo */}
                          {quickDates.length > 0 && (
                            <div className="mb-3">
                              <p className="text-[10px] text-[#73706A] mb-1">{t('common.preferredDate')}:</p>
                              <div className="flex flex-wrap gap-1">
                                {quickDates.map(d => (
                                  <button
                                    key={d}
                                    type="button"
                                    onClick={() => setAvulsaDates({ ...avulsaDates, [key]: d })}
                                    className={`text-[11px] px-2 py-1 rounded border transition-colors ${
                                      date === d
                                        ? 'bg-[#E85D04] border-[#E85D04] text-white'
                                        : 'bg-white border-[#E6E2D8] text-[#1A1A1A] hover:border-[#E85D04]'
                                    }`}
                                    data-testid={`avulsa-quick-${key}-${d}`}
                                  >
                                    {formatDateBR(d)}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="mt-auto space-y-2">
                            <Button
                              onClick={() => handleAvulsaCard(key)}
                              disabled={avulsaSaving !== null || !date || !!dateError}
                              className="w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                              data-testid={`avulsa-card-pay-${key}`}
                            >
                              <CreditCard size={16} className="mr-2" />
                              {avulsaSaving === key ? 'Processando...' : (i18n.language === 'en' ? 'Pay with Card' : 'Pagar com Cartao')}
                            </Button>
                            <Button
                              onClick={() => handleAvulsaZelle(key)}
                              disabled={avulsaSaving !== null || !date || !!dateError}
                              variant="outline"
                              className="w-full border-[#0077B6] text-[#0077B6] hover:bg-blue-50"
                              data-testid={`avulsa-zelle-pay-${key}`}
                            >
                              <Phone size={16} className="mr-2" />
                              Pagar via Zelle
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* My Avulsas history */}
            {avulsas.length > 0 && (
              <div className="bg-white border border-[#E6E2D8] rounded-lg p-6" data-testid="avulsa-history">
                <h3 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <ListChecks size={20} className="text-[#73706A]" /> Minhas inscricoes avulsas
                </h3>
                <div className="space-y-2">
                  {avulsas.map(a => (
                    <div key={`${a.category}-${a.id}`} className="flex items-center justify-between p-3 border border-[#E6E2D8] rounded-lg" data-testid={`avulsa-history-${a.id}`}>
                      <div className="flex-1">
                        <p className="font-medium text-sm text-[#1A1A1A]">{a.label}</p>
                        <p className="text-xs text-[#73706A]">Data: {a.preferred_date} - ${a.amount.toFixed(2)}</p>
                      </div>
                      <span className={`badge ${a.payment_status === 'pago' ? 'badge-success' : 'badge-warning'}`}>
                        {a.payment_status === 'pago' ? t('portal.paid') : t('portal.pending')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Aluguel de Quadra Tab */}
          <TabsContent value="rental" className="space-y-6 mt-0">
            <CourtRentalPortal user={user} />
          </TabsContent>

          {/* Store Tab */}
          <TabsContent value="store" className={`space-y-6 mt-0 ${cart.length > 0 ? 'pb-24' : ''}`}>
            <MyOrders refreshKey={cart.length} />
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] flex items-center gap-2">
                  <TShirt size={20} className="text-[#E85D04]" /> Uniformes
                </h2>
                <Button size="sm" variant="outline" className="relative border-[#E85D04] text-[#E85D04] hover:bg-[#E85D04] hover:text-white" onClick={openPortalCart} data-testid="store-cart-header-btn">
                  <ShoppingCart size={16} className="mr-1.5" /> Carrinho
                  <span className="ml-1.5 min-w-[20px] h-5 px-1 rounded-full bg-[#E85D04] text-white text-[11px] font-bold inline-flex items-center justify-center" data-testid="store-cart-count">{cartCount}</span>
                </Button>
              </div>
              {storeProducts.length === 0 ? (
                <p className="text-[#73706A] text-center py-8">{i18n.language === 'en' ? 'No products available right now' : 'Nenhum produto disponivel no momento'}</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {storeProducts.map((product, i) => {
                    const cartLines = cart.filter(c => c.product_id === product.id);
                    return (
                      <div key={product.id} className="border border-[#E6E2D8] rounded-lg p-4" data-testid={`store-product-${i}`}>
                        {(() => {
                          const imgCount = (product.images && product.images.length) ? product.images.length : (product.image_url ? 1 : 0);
                          const active = storeActiveImg[product.id] || 0;
                          return imgCount > 0 ? (
                            <>
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/product-image/${product.id}/${active}`}
                                alt={product.name}
                                className="w-full h-40 object-contain bg-[#F5F1E8] rounded-lg mb-2"
                                onError={(e) => { e.target.style.display = 'none'; }}
                                data-testid={`store-product-image-${i}`}
                              />
                              {imgCount > 1 && (
                                <div className="flex gap-1.5 mb-3 flex-wrap">
                                  {Array.from({ length: imgCount }).map((_, idx) => (
                                    <button key={idx} onClick={() => setStoreActiveImg(p => ({ ...p, [product.id]: idx }))}
                                      className={`w-9 h-9 rounded overflow-hidden border-2 ${active === idx ? 'border-[#E85D04]' : 'border-[#E6E2D8]'}`}
                                      data-testid={`store-thumb-${i}-${idx}`}>
                                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/product-image/${product.id}/${idx}`} alt="" className="w-full h-full object-cover" />
                                    </button>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : null;
                        })()}
                        <h3 className="font-medium text-[#1A1A1A]">{product.name}</h3>
                        {product.description && <p className="text-xs text-[#73706A] mt-1">{product.description}</p>}
                        <p className="text-lg font-bold text-[#E85D04] mt-2">$ {(product.price || 0).toFixed(2)}</p>

                        {(product.sizes?.length > 0 || product.colors?.length > 0) && (
                          <div className="mt-2 space-y-2.5">
                            {product.sizes?.length > 0 && (
                              <OptionChips label="Tamanho" options={product.sizes} value={product._pickSize || autoPick(product.sizes)}
                                onChange={(v) => setStoreProducts(prev => prev.map(p => p.id === product.id ? { ...p, _pickSize: v } : p))}
                                testid={`store-pick-size-${i}`} />
                            )}
                            {product.colors?.length > 0 && (
                              <OptionChips label="Cor" options={product.colors} value={product._pickColor || autoPick(product.colors)}
                                onChange={(v) => setStoreProducts(prev => prev.map(p => p.id === product.id ? { ...p, _pickColor: v } : p))}
                                testid={`store-pick-color-${i}`} />
                            )}
                          </div>
                        )}

                        <Button size="sm" className="w-full mt-3 bg-[#E85D04] hover:bg-[#DC2F02] text-white" onClick={() => {
                          const size = product._pickSize || autoPick(product.sizes);
                          const color = product._pickColor || autoPick(product.colors);
                          const err = missingOption(product, size, color);
                          if (err) { toast.error(err); return; }
                          const key = `${product.id}__${size}__${color}`;
                          const idx = cart.findIndex(c => `${c.product_id}__${c.size || ''}__${c.color || ''}` === key);
                          if (idx >= 0) {
                            const next = [...cart];
                            next[idx] = { ...next[idx], quantity: (next[idx].quantity || 1) + 1 };
                            setCart(next);
                          } else {
                            setCart([...cart, { product_id: product.id, product_name: product.name, quantity: 1, size, color, price: product.price, basePrice: product.price, tiers: product.promo_tiers || [] }]);
                          }
                          toast.success(`${product.name} adicionado`);
                        }} data-testid={`store-add-${i}`}>
                          <ShoppingCart size={16} className="mr-1" /> Adicionar
                        </Button>

                        {cartLines.length > 0 && (
                          <p className="text-[11px] text-[#2D6A4F] mt-2 text-center" data-testid={`store-incart-${i}`}>
                            {cartLines.reduce((s, l) => s + (l.quantity || 1), 0)} no carrinho
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {storeCombos.length > 0 && (
              <div className="bg-white border border-[#E6E2D8] rounded-lg p-6" data-testid="store-combos-section">
                <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
                  <TShirt size={20} className="text-[#E85D04]" /> Promoções
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {storeCombos.map((combo, i) => (
                    <div key={combo.id} className="border-2 border-[#E85D04]/30 rounded-lg p-4" data-testid={`store-combo-${i}`}>
                      {combo.has_image && (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/combo-image/${combo.id}/0`} alt={combo.name} className="w-full h-36 object-contain bg-[#F5F1E8] rounded-lg mb-3" onError={(e) => { e.target.style.display = 'none'; }} />
                      )}
                      <span className="text-[10px] font-bold uppercase bg-[#E85D04] text-white px-2 py-0.5 rounded">Promoção</span>
                      <h3 className="font-medium text-[#1A1A1A] mt-2">{combo.name}</h3>
                      {combo.description && <p className="text-xs text-[#73706A] mt-1">{combo.description}</p>}
                      <ul className="text-sm text-[#1A1A1A] mt-2 space-y-0.5">
                        {combo.items.map((it, idx) => (<li key={idx}>• {it.quantity}x {it.product_name}</li>))}
                      </ul>
                      <p className="text-lg font-bold text-[#E85D04] mt-2">$ {(combo.price || 0).toFixed(2)}</p>
                      <Button size="sm" className="w-full mt-3 bg-[#E85D04] hover:bg-[#DC2F02] text-white" onClick={() => setPortalComboPicking(combo)} data-testid={`store-combo-add-${i}`}>
                        <ShoppingCart size={16} className="mr-1" /> Escolher tamanhos e cores
                      </Button>
                    </div>
                  ))}
                </div>
                <ComboPickerDialog combo={portalComboPicking} onClose={() => setPortalComboPicking(null)} onConfirm={(selections) => {
                  const combo = portalComboPicking;
                  setCart(prev => [...prev, { uid: `${combo.id}-${Date.now()}`, combo_id: combo.id, is_combo: true, product_name: combo.name, quantity: 1, price: combo.price, basePrice: combo.price, selections }]);
                  setPortalComboPicking(null);
                  toast.success(`${combo.name} adicionado`);
                }} />
              </div>
            )}


            {cart.length > 0 && (
              <button type="button" onClick={openPortalCart} className="fixed bottom-5 right-5 z-40 flex items-center gap-2 h-12 pl-4 pr-5 rounded-full bg-[#E85D04] text-white font-semibold shadow-lg hover:bg-[#DC2F02] transition-colors" data-testid="store-cart-floating-btn">
                <ShoppingCart size={20} /> Ver carrinho ({cartCount})
              </button>
            )}

            {cart.length > 0 && (
              <div id="portal-cart" className="bg-white border-2 border-[#E85D04] rounded-lg p-6 scroll-mt-4" data-testid="store-cart-section">
                <h3 className="font-['Outfit'] font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <ShoppingCart size={18} /> Carrinho ({cart.reduce((s, c) => s + (c.quantity || 1), 0)})
                </h3>
                <div className="space-y-2 mb-4">
                  {cart.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-[#F5F2EB] rounded">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.product_name}</p>
                        <CartLineOptions line={item} />
                      </div>
                      <div className={`flex items-center gap-1 ${item.is_combo ? 'invisible' : ''}`}>
                        <button
                          onClick={() => setCart(prev => prev.map((c, idx) => idx === i ? { ...c, quantity: Math.max(1, (c.quantity || 1) - 1) } : c))}
                          className="w-6 h-6 rounded border border-[#E6E2D8] text-[#73706A] text-sm leading-none"
                          data-testid={`cart-dec-${i}`}
                        >−</button>
                        <span className="w-6 text-center text-sm font-semibold" data-testid={`cart-qty-${i}`}>{item.quantity}</span>
                        <button
                          onClick={() => setCart(prev => prev.map((c, idx) => idx === i ? { ...c, quantity: (c.quantity || 1) + 1 } : c))}
                          className="w-6 h-6 rounded border border-[#E6E2D8] text-[#73706A] text-sm leading-none"
                          data-testid={`cart-inc-${i}`}
                        >+</button>
                      </div>
                      <span className="text-sm font-bold text-[#E85D04] w-16 text-right">$ {(storeEffUnit(item.basePrice ?? item.price, item.tiers, item.quantity) * (item.quantity || 1)).toFixed(2)}</span>
                      <button
                        onClick={() => setCart(prev => prev.filter((_, idx) => idx !== i))}
                        className="text-[#9B2226] text-sm px-1"
                        data-testid={`cart-remove-${i}`}
                      >×</button>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t border-[#E6E2D8]">
                    <span className="font-semibold">{i18n.language === 'en' ? 'Subtotal' : 'Subtotal'}</span>
                    <span className="font-bold text-[#1A1A1A]">$ {cart.reduce((s, c) => s + storeEffUnit(c.basePrice ?? c.price, c.tiers, c.quantity) * (c.quantity || 1), 0).toFixed(2)}</span>
                  </div>
                  {storePaymentMode === 'deposit' && (
                    <div className="flex justify-between text-sm text-[#E85D04] font-medium" data-testid="store-deposit-row">
                      <span>Pagar agora (50%)</span>
                      <span>$ {(cart.reduce((s, c) => s + storeEffUnit(c.basePrice ?? c.price, c.tiers, c.quantity) * (c.quantity || 1), 0) / 2).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs text-[#73706A]">
                    <span>Cartao (+5%)</span>
                    <span>$ {((storePaymentMode === 'deposit' ? cart.reduce((s, c) => s + storeEffUnit(c.basePrice ?? c.price, c.tiers, c.quantity) * (c.quantity || 1), 0) / 2 : cart.reduce((s, c) => s + storeEffUnit(c.basePrice ?? c.price, c.tiers, c.quantity) * (c.quantity || 1), 0)) * 1.05).toFixed(2)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button type="button" onClick={() => setStorePaymentMode('full')} className={`p-2.5 rounded-lg border text-sm text-left transition-colors ${storePaymentMode === 'full' ? 'border-[#E85D04] bg-orange-50' : 'border-[#E6E2D8]'}`} data-testid="store-mode-full">
                    <span className="font-medium">Valor total</span>
                    <span className="block text-[11px] text-[#73706A]">Pagar tudo agora</span>
                  </button>
                  <button type="button" onClick={() => setStorePaymentMode('deposit')} className={`p-2.5 rounded-lg border text-sm text-left transition-colors ${storePaymentMode === 'deposit' ? 'border-[#E85D04] bg-orange-50' : 'border-[#E6E2D8]'}`} data-testid="store-mode-deposit">
                    <span className="font-medium">Pré-reserva 50%</span>
                    <span className="block text-[11px] text-[#73706A]">Metade na entrega</span>
                  </button>
                </div>

                {storeZelleOrderId ? (
                  <div className="space-y-2 bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="store-zelle-step">
                    <p className="text-sm text-blue-900">Envie via Zelle para <strong>5618596830</strong> e anexe o comprovante:</p>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" disabled={uploadingStoreZelle}
                      onChange={async (e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        setUploadingStoreZelle(true);
                        try {
                          const fd = new FormData(); fd.append('file', file);
                          await axios.post(`${API}/orders/zelle-receipt?order_id=${storeZelleOrderId}&phase=auto`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
                          toast.success(t('portal.receiptSent'));
                          setCart([]); setStoreZelleOrderId(null); fetchData();
                        } catch (err) { toast.error(err.response?.data?.detail || t('portal.errorGeneric')); }
                        finally { setUploadingStoreZelle(false); }
                      }}
                      className="block w-full text-sm" data-testid="store-zelle-upload" />
                    <button onClick={() => setStoreZelleOrderId(null)} className="text-xs text-[#73706A] underline">Cancelar</button>
                  </div>
                ) : (
                <div className="space-y-2">
                  <Button
                    className="w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                    disabled={orderPlacing}
                    onClick={async () => {
                      setOrderPlacing(true);
                      try {
                        const { data: order } = await axios.post(`${API}/portal/orders`, { payment_mode: storePaymentMode, items: cart.filter(c => !c.is_combo).map(c => ({ product_id: c.product_id, quantity: c.quantity, size: c.size, color: c.color })), combos: cart.filter(c => c.is_combo).map(c => ({ combo_id: c.combo_id, quantity: 1, selections: c.selections })) }, { withCredentials: true });
                        // Create Stripe checkout for the order
                        const { data: checkout } = await axios.post(`${API}/portal/universal-checkout`, {
                          item_id: order.id,
                          item_type: 'Uniforme',
                          origin_url: window.location.origin
                        }, { withCredentials: true });
                        if (checkout.url) {
                          const a = document.createElement('a'); a.href = checkout.url; a.rel = 'noopener'; document.body.appendChild(a); a.click(); document.body.removeChild(a);
                        }
                      } catch (e) { toast.error(e.response?.data?.detail || t('portal.errorGeneric')); setOrderPlacing(false); }
                    }}
                    data-testid="place-order-card-btn"
                  >
                    {orderPlacing ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CreditCard size={16} className="mr-2" /> {storePaymentMode === 'deposit' ? 'Pagar 50% com Cartao' : 'Pagar com Cartao'}</>}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white"
                    disabled={orderPlacing}
                    onClick={async () => {
                      setOrderPlacing(true);
                      try {
                        const { data: order } = await axios.post(`${API}/portal/orders`, { payment_mode: storePaymentMode, items: cart.filter(c => !c.is_combo).map(c => ({ product_id: c.product_id, quantity: c.quantity, size: c.size, color: c.color })), combos: cart.filter(c => c.is_combo).map(c => ({ combo_id: c.combo_id, quantity: 1, selections: c.selections })) }, { withCredentials: true });
                        navigator.clipboard?.writeText('5618596830')?.catch(() => {});
                        setStoreZelleOrderId(order.id);
                        toast.success('Pedido criado! Envie via Zelle e anexe o comprovante.');
                      } catch (e) { toast.error(e.response?.data?.detail || t('portal.errorGeneric')); }
                      finally { setOrderPlacing(false); }
                    }}
                    data-testid="place-order-zelle-btn"
                  >
                    <Phone size={16} className="mr-2" /> {storePaymentMode === 'deposit' ? 'Pagar 50% via Zelle' : 'Pagar via Zelle'}
                  </Button>
                </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* My All Payments Tab */}
          <TabsContent value="my-payments" className="space-y-6 mt-0">
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
              <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
                <Wallet size={20} className="text-[#E85D04]" />
                Historico de Pagamentos
              </h2>
              {allMyPayments.length === 0 ? (
                <p className="text-[#73706A] text-center py-8">{t('portal.noPaymentsYet')}</p>
              ) : (
                <div className="space-y-3">
                  {allMyPayments.map((p, i) => {
                    const typeColors = { 'Mensalidade': '#2D6A4F', 'TK League': '#E85D04', 'Aula Avulsa': '#0077B6', 'Uniforme': '#9B2226' };
                    const color = typeColors[p.type] || '#73706A';
                    const isPending = p.status !== 'pago';
                    return (
                      <div key={`${p.type}-${p.id}`} className={`p-4 rounded-lg ${isPending ? 'bg-orange-50 border border-orange-200' : 'bg-[#F5F2EB]'}`} data-testid={`my-payment-${i}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: color }}>
                              {p.type === 'Mensalidade' && <Money size={18} className="text-white" />}
                              {p.type === 'TK League' && <Trophy size={18} className="text-white" />}
                              {p.type === 'Aula Avulsa' && <CalendarDots size={18} className="text-white" />}
                              {p.type === 'Uniforme' && <CreditCard size={18} className="text-white" />}
                            </div>
                            <div>
                              <p className="font-medium text-[#1A1A1A] text-sm">{p.type}</p>
                              <p className="text-xs text-[#73706A]">{p.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-[#1A1A1A]">$ {(p.amount || 0).toFixed(2)}</span>
                            <span className={`badge text-xs ${p.status === 'pago' ? 'badge-success' : 'badge-warning'}`}>
                              {p.status}
                            </span>
                          </div>
                        </div>
                        {isPending && (
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-orange-200 flex-wrap">
                            <Button
                              size="sm"
                              className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                              onClick={async () => {
                                setStripeLoading(p.id);
                                try {
                                  const originUrl = window.location.origin;
                                  let response;
                                  if (p.type === 'Mensalidade') {
                                    response = await axios.post(`${API}/portal/stripe-checkout`, {
                                      payment_id: p.id,
                                      origin_url: originUrl
                                    }, { withCredentials: true });
                                  } else {
                                    response = await axios.post(`${API}/portal/universal-checkout`, {
                                      item_id: p.id,
                                      item_type: p.type,
                                      origin_url: originUrl
                                    }, { withCredentials: true });
                                  }
                                  if (response.data.url) {
                                    const a = document.createElement('a');
                                    a.href = response.data.url;
                                    a.rel = 'noopener';
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                  } else {
                                    toast.error('Erro ao gerar link de pagamento');
                                    setStripeLoading(null);
                                  }
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Erro ao iniciar pagamento');
                                  setStripeLoading(null);
                                }
                              }}
                              disabled={stripeLoading === p.id}
                              data-testid={`mypay-card-${i}`}
                            >
                              {stripeLoading === p.id ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <><CreditCard size={16} className="mr-1" /> Cartao</>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white"
                              onClick={() => {
                                navigator.clipboard.writeText('5618596830');
                                toast.success('Numero Zelle copiado! Envie $' + p.amount.toFixed(2) + ' para 5618596830');
                              }}
                              data-testid={`mypay-zelle-${i}`}
                            >
                              <Phone size={16} className="mr-1" /> Zelle
                            </Button>
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                className="hidden"
                                onChange={(e) => handleZelleUpload(p.id, e.target.files[0])}
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white pointer-events-none"
                                disabled={uploadingZelle === p.id}
                              >
                                {uploadingZelle === p.id ? (
                                  <div className="w-4 h-4 border-2 border-[#2D6A4F] border-t-transparent rounded-full animate-spin mr-1" />
                                ) : (
                                  <UploadSimple size={16} className="mr-1" />
                                )}
                                Comprovante
                              </Button>
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6 mt-0">
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
              <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4">Meus Avisos</h2>
              {notifications.length === 0 ? (
                <p className="text-[#73706A] text-center py-8">{t('portal.noNotifications')}</p>
              ) : (
                <div className="space-y-3">
                  {notifications.map((n, i) => (
                    <div
                      key={n.id}
                      className={`p-4 rounded-lg border ${n.read_by_student ? 'bg-[#F5F2EB] border-transparent' : 'bg-white border-[#E85D04] border-l-4'}`}
                      data-testid={`portal-notif-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${n.type === 'overdue_payment' ? 'bg-[#9B2226]' : 'bg-[#0077B6]'}`}>
                          <Warning size={16} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className={`font-medium ${n.read_by_student ? 'text-[#73706A]' : 'text-[#1A1A1A]'}`}>
                            {n.title}
                          </h4>
                          <p className="text-sm text-[#73706A] mt-1">{n.message}</p>
                          <p className="text-xs text-[#73706A] mt-2">{new Date(n.created_at).toLocaleDateString('pt-BR')}</p>
                        </div>
                        {!n.read_by_student && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#0077B6] hover:bg-[#F5F2EB]"
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/portal/notifications/${n.id}/read`, {}, { withCredentials: true });
                                fetchData();
                              } catch {}
                            }}
                            data-testid={`portal-mark-read-${i}`}
                          >
                            <CheckCircle size={18} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6 mt-0">
            <PortalPasswordChange />
          </TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}
